// src/components/TransportSessionLocations.js

import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";

import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";

import {
  FaGripLines,
  FaMapMarkerAlt,
  FaBullseye,
  FaTrash,
  FaEdit,
  FaCamera,
  FaGasPump,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaCommentDots,
} from "react-icons/fa";

import {
  User,
  CalendarDays,
  Camera,
  Info,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Clock3,
  X,
} from "lucide-react";
import "../styles/TransportSessionLocations.css";
import "../styles/TransportSessionLocationsCompact.css";

import TruckSelectorModal from "./TruckSelectorModal";
import TrailerSelectorModal from "./TrailerSelectorModal";
import DriverSelectorModal from "./DriverSelectorModal";
import FreeTextTruckModal from "./FreeTextTruckModal";
import FreeTextDriverModal from "./FreeTextDriverModal";
import StrategicPointModal from "./StrategicPointModal";
import StatusSelectorModal from "./StatusSelectorModal";
import CMRLoadModal from "./CMRLoadModal";
import TruckInventoryModal from "./TruckInventoryModal";
import TrailerInventoryModal from "./TrailerInventoryModal";
import EditStrategicPointModal from "./EditStrategicPointModal";
import TransportSessionMapModal from "./TransportSessionMapModal";
import ModalPortal from "./ModalPortal";
import { getSocket } from "../lib/socket";
import { useTranslation } from "react-i18next";

const calendarDateFromISO = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));

  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);

  const date = new Date(year, month, day, 12);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const calendarISOFromDate = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const SessionScheduleCalendar = ({
  value,
  position,
  locale = "en-GB",
  onSelect,
  onClose,
}) => {
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const date = calendarDateFromISO(value) || new Date();

    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [onClose]);

  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();

  const firstDay = new Date(year, month, 1);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cellCount = Math.ceil((offset + daysInMonth) / 7) * 7;

  const days = Array.from({ length: cellCount }, (_, index) =>
    new Date(year, month, index - offset + 1, 12)
  );

  const todayISO = calendarISOFromDate(new Date());

  const monthTitle = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(visibleMonth);

  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "short",
    }).format(new Date(2024, 0, index + 1))
  );

  const changeMonth = (amount) => {
    setVisibleMonth(
      new Date(year, month + amount, 1)
    );
  };

  return (
    <>
      <div
        className="loadevo-session-calendar-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="loadevo-session-calendar"
        role="dialog"
        aria-modal="true"
        aria-label="Select date"
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div className="loadevo-session-calendar-heading">
          <div className="loadevo-session-calendar-heading-title">
            <CalendarDays size={21} aria-hidden="true" />
            <strong>Select date</strong>
          </div>

          <button
            type="button"
            className="loadevo-session-calendar-close"
            onClick={onClose}
            aria-label="Close calendar"
          >
            ×
          </button>
        </div>

        <div className="loadevo-session-calendar-month">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            aria-label="Previous month"
          >
            <ChevronLeft size={19} />
          </button>

          <strong>{monthTitle}</strong>

          <button
            type="button"
            onClick={() => changeMonth(1)}
            aria-label="Next month"
          >
            <ChevronRight size={19} />
          </button>
        </div>

        <div className="loadevo-session-calendar-grid">
          {weekdays.map((day, index) => (
            <span
              key={`weekday-${index}`}
              className="loadevo-session-calendar-weekday"
            >
              {day}
            </span>
          ))}

          {days.map((day) => {
            const iso = calendarISOFromDate(day);
            const isSelected = iso === value;
            const isToday = iso === todayISO;
            const isOutside = day.getMonth() !== month;

            return (
              <button
                key={iso}
                type="button"
                className={[
                  "loadevo-session-calendar-day",
                  isSelected ? "is-selected" : "",
                  isToday ? "is-today" : "",
                  isOutside ? "is-outside" : "",
                ].filter(Boolean).join(" ")}
                aria-label={new Intl.DateTimeFormat(locale, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }).format(day)}
                aria-pressed={isSelected}
                onClick={() => onSelect(iso)}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>

        <div className="loadevo-session-calendar-footer">
          <button type="button" onClick={onClose}>
            Cancel
          </button>

          <button
            type="button"
            className="is-today-action"
            onClick={() => onSelect(todayISO)}
          >
            Today
          </button>
        </div>
      </div>
    </>
  );
};

const SessionConflictTimePicker = ({
  kind,
  initialType,
  initialStart,
  initialEnd,
  position,
  onClose,
  onApply,
}) => {
  const [mode, setMode] = useState(
    String(initialType).toUpperCase() === "WINDOW"
      ? "WINDOW"
      : "FIXED"
  );

  const [start, setStart] = useState(
    String(initialStart || "09:00").slice(0, 5)
  );

  const [end, setEnd] = useState(
    String(initialEnd || "16:00").slice(0, 5)
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () =>
      document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  const validTime = (value) =>
    /^([01]\d|2[0-3]):[0-5]\d$/.test(value || "");

  const canApply =
    validTime(start) &&
    (mode === "FIXED" || (validTime(end) && end >= start));

  return (
    <>
      <div
        className="loadevo-session-time-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="loadevo-session-time-popup"
        style={{
          top: position.top,
          left: position.left,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={`${kind} schedule`}
      >
        <div className="loadevo-session-time-heading">
          <div>
            <CalendarDays size={22} />
            <strong>
              {kind === "delivery"
                ? "Delivery schedule"
                : "Pickup schedule"}
            </strong>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={19} />
          </button>
        </div>

        <div className="loadevo-session-time-modes">
          <button
            type="button"
            className={`loadevo-session-time-mode ${
              mode === "FIXED" ? "is-selected" : ""
            }`}
            onClick={() => setMode("FIXED")}
            aria-pressed={mode === "FIXED"}
          >
            <span className="loadevo-session-time-mode-icons">
              <Clock3 size={21} />
              {mode === "FIXED" && <CheckCircle2 size={20} />}
            </span>

            <strong>Fixed time</strong>
            <small>Specific appointment</small>
          </button>

          <button
            type="button"
            className={`loadevo-session-time-mode ${
              mode === "WINDOW" ? "is-selected" : ""
            }`}
            onClick={() => setMode("WINDOW")}
            aria-pressed={mode === "WINDOW"}
          >
            <span className="loadevo-session-time-mode-icons">
              <CalendarDays size={21} />
              {mode === "WINDOW" && <CheckCircle2 size={20} />}
            </span>

            <strong>Time window</strong>
            <small>Flexible arrival</small>
          </button>
        </div>

        <div
          className={`loadevo-session-time-fields ${
            mode === "WINDOW" ? "is-window" : ""
          }`}
        >
          <label>
            <span>{mode === "WINDOW" ? "From" : "Appointment time"}</span>
            <input
              type="time"
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>

          {mode === "WINDOW" && (
            <label>
              <span>Until</span>
              <input
                type="time"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            </label>
          )}
        </div>

        {mode === "WINDOW" && validTime(start) &&
          validTime(end) && end < start && (
            <p className="loadevo-session-time-error">
              The end time must be after the start time.
            </p>
          )}

        <button
          type="button"
          className="loadevo-session-time-apply"
          disabled={!canApply}
          onClick={() =>
            onApply({
              scheduleType: mode,
              hour: start,
              hourEnd: mode === "WINDOW" ? end : null,
            })
          }
        >
          Apply schedule
        </button>
      </div>
    </>
  );
};

const TruckGlyph = ({ className = "" }) => (
  <img
    className={className}
    src="/icons/truck-optimized.webp"
    alt=""
    aria-hidden="true"
    draggable={false}
  />
);

const TrailerGlyph = ({ className = "" }) => (
  <img
    className={className}
    src="/icons/trailer-optimized.webp"
    alt=""
    aria-hidden="true"
    draggable={false}
  />
);

const COUNTRY_CODES = {
  portugal: "PT",
  france: "FR",
  franca: "FR",
  espanha: "ES",
  spain: "ES",
  alemanha: "DE",
  germany: "DE",
  belgica: "BE",
  belgium: "BE",
  netherlands: "NL",
  paisesbaixos: "NL",
  holanda: "NL",
  luxembourg: "LU",
  luxemburgo: "LU",
  italy: "IT",
  italia: "IT",
  switzerland: "CH",
  suica: "CH",
  austria: "AT",
  poland: "PL",
  polonia: "PL",
  unitedkingdom: "GB",
  "reino unido": "GB",
  ireland: "IE",
  irlanda: "IE",
};

const getCountryFlag = (country) => {
  const normalized = String(country || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

  const key = normalized.replace(/[\s-]/g, "");
  const code =
    COUNTRY_CODES[normalized] ||
    COUNTRY_CODES[key] ||
    (/^[a-z]{2}$/.test(normalized) ? normalized.toUpperCase() : "");

  if (!code) return "";

  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
};

/* ------------------------- Carrier helper ------------------------- */
function getMyCompanyIdSafe() {
  try {
    const direct =
      localStorage.getItem("companyId") ||
      localStorage.getItem("company_id") ||
      null;
    if (direct) return direct;

    const companyRaw = localStorage.getItem("company");
    if (companyRaw) {
      const obj = JSON.parse(companyRaw);
      return obj?.id ?? obj?.companyId ?? obj?.company_id ?? null;
    }
  } catch {}
  return null;
}

function isExternalTransportCarrier(load) {
  const myCompanyId = getMyCompanyIdSafe();
  const myId = myCompanyId != null ? String(myCompanyId) : null;

  // Se não sei quem sou, não arrisco "external"
  if (!myId) return false;

  // ✅ Campo que tu já usas no Loads.js e está no summary-with-progress
  const carrierId =
    load?.carrierId ??
    load?.carrier_id ??
    load?.carrier?.id ??
    load?.transportCarrierId ??           // fallback ok
    load?.transport_carrier_id ??
    load?.transportCarrier?.id ??
    null;

  // Se não vem carrierId, não assumes external aqui (senão dá falsos positivos)
  if (carrierId == null) return false;

  return String(carrierId) !== myId;
}

// 🔎 Debug helper (enable with: localStorage.setItem("debugTruckRouting","1"))
// 🔎 Debug helper (enable with: localStorage.setItem("debugTruckRouting","1"))
const dbgTruck = (...args) => {
  try {
    if (localStorage.getItem("debugTruckRouting") === "1") {
      console.log(...args);
    }
  } catch {}
};

// 🔎 Driver routing debug (enable with: localStorage.setItem("debugDriverRouting","1"))
const dbgDriver = (...args) => {
  try {
    if (localStorage.getItem("debugDriverRouting") === "1") {
      console.log(...args);
    }
  } catch {}
};

// --- Derive display values from "manual" load info fields (external carriers) ---
const stripManualInfo = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return null;
  // supports: "AA-11-BB • manual" or "John Doe • manual"
  const left = s.split("•")[0].trim();
  return left || null;
};

const normalizeExternalDisplay = (row) => {
  const r = row || {};
  const typeUpper = String(r.type || "").toUpperCase();

  const loadCarrierExternal = !!r?.load && isExternalTransportCarrier(r.load);

  const truckInfo =
    typeUpper === "DELIVERY"
      ? (r.truckDeliveryInfo ?? r.load?.truckDeliveryInfo ?? null)
      : (r.truckPickupInfo ?? r.load?.truckPickupInfo ?? null);

  const driverInfo =
    typeUpper === "DELIVERY"
      ? (r.driverDeliveryInfo ?? r.load?.driverDeliveryInfo ?? null)
      : (r.driverPickupInfo ?? r.load?.driverPickupInfo ?? null);

  const trailerInfo =
    typeUpper === "DELIVERY"
      ? (r.trailerDeliveryInfo ?? r.load?.trailerDeliveryInfo ?? null)
      : (r.trailerPickupInfo ?? r.load?.trailerPickupInfo ?? null);

  const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

  const hasTruckIdField = hasOwn(r, "truckId");
  const hasTruckPlateField = hasOwn(r, "truckPlate");
  const hasDriverIdField = hasOwn(r, "driverId");
  const hasDriverNameField = hasOwn(r, "driverName");
  const hasTrailerIdField = hasOwn(r, "trailerId");
  const hasTrailerPlateField = hasOwn(r, "trailerPlate");

  const shouldFallbackTruck =
    loadCarrierExternal &&
    !hasTruckIdField &&
    !hasTruckPlateField;

  const shouldFallbackDriver =
    loadCarrierExternal &&
    !hasDriverIdField &&
    !hasDriverNameField;

  const shouldFallbackTrailer =
    loadCarrierExternal &&
    !hasTrailerIdField &&
    !hasTrailerPlateField;

  const manualTruckPlate = shouldFallbackTruck ? stripManualInfo(truckInfo) : null;
  const manualDriverName = shouldFallbackDriver ? stripManualInfo(driverInfo) : null;
  const manualTrailerPlate = shouldFallbackTrailer ? stripManualInfo(trailerInfo) : null;

  return {
    ...r,
    truckPlate: hasTruckPlateField ? r.truckPlate : (r.truckPlate ?? manualTruckPlate),
    driverName: hasDriverNameField ? r.driverName : (r.driverName ?? manualDriverName),
    trailerPlate: hasTrailerPlateField ? r.trailerPlate : (r.trailerPlate ?? manualTrailerPlate),
  };
};

function getAllPickups(loads, loadId) {
  const load = loads.find((l) => l.id === loadId);
  return load?.pickupDeliveries?.filter((pd) => pd.type === "PICKUP") || [];
}
function getAllDeliveries(loads, loadId) {
  const load = loads.find((l) => l.id === loadId);
  return load?.pickupDeliveries?.filter((pd) => pd.type === "DELIVERY") || [];
}
function getSessionLoads(loads, transportsessionId) {
  const sid = Number(transportsessionId);
  return (loads || []).filter((l) => {
    const lSid =
      l?.transportsessionId ??
      l?.transportSessionId ??
      l?.transportsession_id ??
      l?.transport_session_id ??
      null;

    return lSid != null && Number(lSid) === sid;
  });
}

function getRepresentativeLoadForSession(loads, transportsessionId) {
  const sessionLoads = getSessionLoads(loads, transportsessionId);

  // 1) se só houver 1 load, perfeito
  if (sessionLoads.length === 1) return sessionLoads[0] || null;

  // 2) se houver várias, tenta escolher uma “ativa” / não finalizada
  const notFinal = sessionLoads.find((l) => !isLoadFinalized(l));
  if (notFinal) return notFinal;

  // 3) fallback: primeira
  return sessionLoads[0] || null;
}

const CLOSED_STATUSES = ["COMPLETED", "CANCELLED"];

const isFuelStrategicPoint = (location) =>
  String(location?.type || "").toUpperCase() === "STRATEGICPOINT" &&
  String(location?.strategicPointPurpose || "").toUpperCase() === "FUEL";

const hasTrailerSwapEnabled = (location) => {
  if (!location) return false;

  if (
    location.hasTrailerSwap === true ||
    String(location.hasTrailerSwap || "").toLowerCase() === "true"
  ) {
    return true;
  }

  const arrivalValue =
    location.trailerIdArrival ??
    location.trailerArrivalPlate ??
    "";

  const departureValue =
    location.trailerIdDeparture ??
    location.trailerDeparturePlate ??
    "";

  const arrivalText = String(arrivalValue || "").trim();
  const departureText = String(departureValue || "").trim();

  return !!arrivalText && !!departureText && arrivalText !== departureText;
};

const isLoadFinalized = (load) => {
  const s = String(load?.status ?? "").toUpperCase();
  return s === "COMPLETED" || s === "CANCELLED";
};

const hasStoredDocPhotos = async (pickupDeliveryId) => {
  const { data } = await axios.get(
    `/pickupdeliveries/${pickupDeliveryId}/docPhotos`
  );

  const photos = Array.isArray(data?.docPhotos)
    ? data.docPhotos
    : [];

  return photos.some((photo) => {
    if (typeof photo === "string") {
      return photo.trim().length > 0;
    }

    return Boolean(
      String(photo?.url || photo?.key || "").trim()
    );
  });
};

const reorderCooldownUntil = new Map(); // transportsessionId -> timestamp (ms)

const SessionSortableStop = ({
  id,
  disabled,
  dragInProgress,
  children,
}) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(id),
    disabled,

    // Prevent residual layout animations after dropping.
    animateLayoutChanges: () => false,

    transition: {
      duration: 180,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    },
  });

  const activeTransform =
    dragInProgress && transform
      ? CSS.Translate.toString({
          ...transform,
          y: 0,
        })
      : undefined;

  return children({
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    isDragging,
    style: {
      transform: activeTransform,

      transition:
        dragInProgress && !isDragging
          ? transition
          : "none",

      zIndex: isDragging ? 100 : undefined,
    },
  });
};

const TransportSessionLocations = forwardRef(
  (
    {
      transportsessionId,
      refreshLoads,
      loads = [],
      setPendingCmrUploads,
      onCmrFinalized = () => {},
      frozenLoadIdsForSession = [],
      freezeStrategicPoints = false,
      onPointsChanged = () => {},
      isClosingSession = false,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation(["transport_session_locations", "status_selector_modal"]);
    const navigate = useNavigate();

    const tStatus = (key, fallback) =>
      t(`status_selector_modal:${key}`, { defaultValue: fallback });

    const tl = (key, fallback, opts = {}) =>
      t(key, { defaultValue: fallback, ...opts });

    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sessionMotionReady, setSessionMotionReady] = useState(false);

    const [modalData, setModalData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [existingDateHours, setExistingDateHours] = useState([]);
    const [sessionSchedulePopup, setSessionSchedulePopup] = useState(null);

    const listRef = useRef(null);
    const [sessionCalendar, setSessionCalendar] = useState(null);
    const [resetKey, setResetKey] = useState(0);
    const [dragInProgress, setDragInProgress] = useState(false);

    const [draftOrder, setDraftOrder] = useState(null);

    const queuedWsRefresh = useRef(false);

    const dndSensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 7 },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const [truckModalOpen, setTruckModalOpen] = useState(false);
    const [selectedPD, setSelectedPD] = useState(null);

    const [trailerModalOpen, setTrailerModalOpen] = useState(false);
    const [selectedPDForTrailer, setSelectedPDForTrailer] = useState(null);

    // Vehicle registration plates
    const [trailersById, setTrailersById] = useState({});
    const [trucksById, setTrucksById] = useState({});
    const plateRequestsInFlight = useRef(new Set());

    // 🆕 Free-text truck (external carriers)
    const [freeTextTruckModalOpen, setFreeTextTruckModalOpen] = useState(false);
    const [freeTextTruckCtx, setFreeTextTruckCtx] = useState(null);
    // shape: { loadId, type: "PICKUP" | "DELIVERY" }

    // 🆕 Free-text driver (external carriers)
    const [freeTextDriverModalOpen, setFreeTextDriverModalOpen] = useState(false);
    const [freeTextDriverCtx, setFreeTextDriverCtx] = useState(null);
    // shape: { loadId, type: "PICKUP" | "DELIVERY" }

    const [driverModalOpen, setDriverModalOpen] = useState(false);
    const [selectedPDForDriver, setSelectedPDForDriver] = useState(null);
    const [selectedDriverSlot, setSelectedDriverSlot] = useState(1);

    // NEW CODE BITES
    const loadsByIdRef = useRef({});

    // sempre que loads mudar, atualiza o map
    useEffect(() => {
      const map = {};
      for (const l of (loads || [])) {
        if (l?.id != null) map[String(l.id)] = l;
      }
      loadsByIdRef.current = map;
    }, [loads]);

    useEffect(() => {
      if (driverModalOpen) {
        console.log("[TSL][DriverModal] OPEN -> DriverSelectorModal (from TransportSessionLocations.js)", {
          transportsessionId,
          selectedPDForDriverId: selectedPDForDriver?.id,
          selectedPDForDriverType: selectedPDForDriver?.type,
          loadId: selectedPDForDriver?.loadId,
        });
        console.trace("[TSL][DriverModal] stack");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverModalOpen]);
    
    useEffect(() => {
      if (freeTextDriverModalOpen) {
        console.log("[TSL][DriverModal] OPEN -> FreeTextDriverModal (from TransportSessionLocations.js)", {
          transportsessionId,
          ctx: freeTextDriverCtx,
        });
        console.trace("[TSL][FreeTextDriverModal] stack");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freeTextDriverModalOpen]);
    

    const [strategicModalOpen, setStrategicModalOpen] = useState(false);

    const [editStrategicModalOpen, setEditStrategicModalOpen] = useState(false);
    const [selectedStrategicPointToEdit, setSelectedStrategicPointToEdit] =
      useState(null);

    const [mapModalOpen, setMapModalOpen] = useState(false);

    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedPDForStatus, setSelectedPDForStatus] = useState(null);

    const locationsRef = useRef([]);
    const isReordering = useRef(false);
    const latestRefreshId = useRef(0);

    const [selectedLoadForCmr, setSelectedLoadForCmr] = useState(null);
    const [cmrLoadModalOpen, setCmrLoadModalOpen] = useState(false);
    const [selectedPdForPhotos, setSelectedPdForPhotos] = useState(null);
    const [photoPresenceById, setPhotoPresenceById] = useState({});
    const [pendingInventoryAfterCmr, setPendingInventoryAfterCmr] = useState(null);

    const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
    const [inventoryTruck, setInventoryTruck] = useState(null);
    const [inventoryData, setInventoryData] = useState(null);
    const [inventorySaving, setInventorySaving] = useState(false);

    const [trailerInventoryModalOpen, setTrailerInventoryModalOpen] = useState(false);
    const [trailerMeta, setTrailerMeta] = useState(null);
    const [trailerInvData, setTrailerInvData] = useState(null);
    const [trailerInvSaving, setTrailerInvSaving] = useState(false);

    const cmpLocations = (a, b) => {
      const aSeq = Number(a.sequence ?? a.newSequence);
      const bSeq = Number(b.sequence ?? b.newSequence);

      if (
        Number.isFinite(aSeq) &&
        Number.isFinite(bSeq) &&
        aSeq !== bSeq
      ) {
        return aSeq - bSeq;
      }

      const aTime = new Date(
        `${String(a.date || "1900-01-01").slice(0, 10)}T${String(a.hour || "00:00").slice(0, 5)}`
      ).getTime();

      const bTime = new Date(
        `${String(b.date || "1900-01-01").slice(0, 10)}T${String(b.hour || "00:00").slice(0, 5)}`
      ).getTime();

      if (
        Number.isFinite(aTime) &&
        Number.isFinite(bTime) &&
        aTime !== bTime
      ) {
        return aTime - bTime;
      }

      return Number(a.id || 0) - Number(b.id || 0);
    };

    const sortLocationsSafe = (rows) => {
      const arr = Array.isArray(rows) ? [...rows] : [];
      arr.sort(cmpLocations);
      return arr;
    };

    const safeDefer = (fn) => {
      if (isReordering.current) {
        queuedWsRefresh.current = true;
        return;
      }

      fn();
    };

    const formatDateLocal = (isoDate) => {
      if (!isoDate) return "";
    
      try {
        const dateOnly = String(isoDate).includes("T")
          ? String(isoDate).split("T")[0]
          : String(isoDate);
    
        const [year, month, day] = dateOnly.split("-");
    
        if (!year || !month || !day) return "";
    
        return `${day}/${month}/${year}`;
      } catch {
        return "";
      }
    };

    const formatStatusEventDateTime = (event) => {
      const raw =
        event?.phoneTimestamp ||
        event?.eventTimestamp ||
        event?.createdAt ||
        null;

      if (!raw) return null;

      try {
        const date = new Date(raw);

        if (Number.isNaN(date.getTime())) return null;

        const locale = i18n.language || "pt-PT";

        const formattedDate = date.toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        const formattedTime = date.toLocaleTimeString(locale, {
          hour: "2-digit",
          minute: "2-digit",
        });

        return `${formattedTime} · ${formattedDate} `;
      } catch {
        return null;
      }
    };

    const formatGpsDistance = (meters) => {
      const n = Number(meters);

      if (!Number.isFinite(n) || n < 0) return null;

      const locale = i18n.language || "pt-PT";

      if (n < 1000) {
        const formattedMeters = Math.round(n).toLocaleString(locale);

        return tl(
          "statusGps.distanceMeters",
          "{{distance}} m de distância",
          { distance: formattedMeters }
        );
      }

      const km = n / 1000;

      const formattedKm = km.toLocaleString(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

      return tl(
        "statusGps.distanceKm",
        "{{distance}} km de distância",
        { distance: formattedKm }
      );
    };

    const joinStatusGpsDetails = (...parts) => {
      return parts
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" · ");
    };

    const getStatusGpsMeta = (location) => {
      const event = location?.latestStatusEvent || null;

      if (!event) {
        return {
          icon: "warning",
          text: tl("statusGps.noEvent", "Sem confirmação GPS"),
          detail: null,
          className: "status-gps-missing",
        };
      }

      const timestampText = formatStatusEventDateTime(event);
      const distanceText = formatGpsDistance(event.distanceFromPlaceMeters);

      const hasGps =
        event.latitude != null &&
        event.longitude != null &&
        String(event.gpsSource || "").toUpperCase() !== "UNAVAILABLE";

      if (!hasGps) {
        return {
          icon: "warning",
          text: tl("statusGps.noGps", "Sem GPS"),
          detail: timestampText,
          className: "status-gps-missing",
        };
      }

      if (event.locationMatch === true) {
        return {
          icon: "location",
          text: tl("statusGps.onLocation", "GPS confirmado no local"),
          detail: timestampText,
          className: "status-gps-ok",
        };
      }

      if (event.locationMatch === false) {
        return {
          icon: "warning",
          text: tl("statusGps.outsideLocation", "GPS fora do local"),
          detail: joinStatusGpsDetails(distanceText, timestampText),
          className: "status-gps-warning",
        };
      }

      return {
        icon: "location",
        text: tl("statusGps.gpsReceived", "GPS recebido"),
        detail: joinStatusGpsDetails(distanceText, timestampText),
        className: "status-gps-neutral",
      };
    };

    const renderStatusGpsIcon = (iconType) => {
      if (iconType === "location") {
        return <MapPin className="status-gps-svg-icon" strokeWidth={2} aria-hidden="true" />;
      }

      if (iconType === "warning") {
        return <AlertTriangle className="status-gps-svg-icon" strokeWidth={2} aria-hidden="true" />;
      }

      return <CheckCircle2 className="status-gps-svg-icon" strokeWidth={2} aria-hidden="true" />;
    };

    const renderStatusMainIcon = (status) => {
      const upper = String(status || "").toUpperCase();

      if (upper === "COMPLETED") {
        return <CheckCircle2 className="status-main-svg-icon" strokeWidth={2} aria-hidden="true" />;
      }

      if (upper === "CANCELLED") {
        return <XCircle className="status-main-svg-icon" strokeWidth={2} aria-hidden="true" />;
      }

      return <Info className="status-main-svg-icon" strokeWidth={2} aria-hidden="true" />;
    };

    const renderStatusButtonContent = (location) => {
      const statusText = location.status
        ? statusLabel(location.status)
        : tl("buttons.status", "Status");

      const gpsMeta = getStatusGpsMeta(location);

      const showGpsMeta =
        Boolean(location.status) &&
        String(location.status).toUpperCase() !== "PENDING";

      return (
        <span className="status-button-content">
          <span className="status-button-main">
            {location.status && renderStatusMainIcon(location.status)}
            <span>{statusText}</span>
          </span>

          {showGpsMeta && (
            <span className={`status-gps-line ${gpsMeta.className}`}>
              <span className="status-gps-summary">
                <span className="status-gps-icon">
                  {renderStatusGpsIcon(gpsMeta.icon)}
                </span>

                <span>{gpsMeta.text}</span>
              </span>

              {gpsMeta.detail && (
                <span className="status-gps-detail">
                  {gpsMeta.detail}
                </span>
              )}
            </span>
          )}
        </span>
      );
    };

    const getDriverDisplayName = (driver) => {
      if (!driver) return null;
      const fromParts = `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim();
      return (
        driver.name ||
        driver.fullName ||
        driver.driverName ||
        (fromParts ? fromParts : null) ||
        driver.email ||
        null
      );
    };

    function softRefresh() {
      if (isReordering.current) {
        queuedWsRefresh.current = true;
        return;
      }

      const refreshId = ++latestRefreshId.current;

      axios
        .get(`/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`)
        .then((res) => {
          if (isReordering.current) {
            queuedWsRefresh.current = true;
            return;
          }

          if (refreshId !== latestRefreshId.current) return;
          const rows = Array.isArray(res.data) ? res.data : [];
          const prevSnap = locationsRef.current || [];

          const normalized = rows.map((r) => {
            const loadFallback = loadsByIdRef.current?.[String(r?.loadId)] || null;

            const prevLoc = prevSnap.find((p) => Number(p?.id) === Number(r?.id)) || null;
            const merged = mergeStrategicFromPrev(r, prevLoc);

            return normalizeExternalDisplay({
              ...merged,
              load: merged?.load ?? loadFallback,
            });
          });
          const sorted = sortLocationsSafe(normalized);

          setLocations(sorted);
          setResetKey((k) => k + 1);

          // ✅ CRITICAL: avisa o Loads.js para recalcular o progress instantaneamente
          onPointsChanged?.(sorted);
        })
        .catch(() => {});
    }

    const releaseReorder = () => {
      isReordering.current = false;
      setDraftOrder(null);

      if (queuedWsRefresh.current) {
        queuedWsRefresh.current = false;
        softRefresh();
      }
    };

    // ✅ helper: preserva campos de StrategicPoint se /light não devolver tudo
    const mergeStrategicFromPrev = (row, prevLoc) => {
      const t = String(row?.type || "").toUpperCase();
      if (t !== "STRATEGICPOINT") return row;

      const out = { ...row };

      if (!out.strategicPointPurpose && prevLoc?.strategicPointPurpose) {
        out.strategicPointPurpose = prevLoc.strategicPointPurpose;
      }

      if (out.hasTrailerSwap == null && prevLoc?.hasTrailerSwap != null) {
        out.hasTrailerSwap = prevLoc.hasTrailerSwap;
      }

      // TRUCK arrival/departure
      if (out.truckIdArrival == null && prevLoc?.truckIdArrival != null) out.truckIdArrival = prevLoc.truckIdArrival;
      if (!out.truckArrivalPlate && prevLoc?.truckArrivalPlate) out.truckArrivalPlate = prevLoc.truckArrivalPlate;

      if (out.truckIdDeparture == null && prevLoc?.truckIdDeparture != null) out.truckIdDeparture = prevLoc.truckIdDeparture;
      if (!out.truckDeparturePlate && prevLoc?.truckDeparturePlate) out.truckDeparturePlate = prevLoc.truckDeparturePlate;

      // TRAILER arrival/departure
      if (out.trailerIdArrival == null && prevLoc?.trailerIdArrival != null) out.trailerIdArrival = prevLoc.trailerIdArrival;
      if (!out.trailerArrivalPlate && prevLoc?.trailerArrivalPlate) out.trailerArrivalPlate = prevLoc.trailerArrivalPlate;

      if (out.trailerIdDeparture == null && prevLoc?.trailerIdDeparture != null) out.trailerIdDeparture = prevLoc.trailerIdDeparture;
      if (!out.trailerDeparturePlate && prevLoc?.trailerDeparturePlate) out.trailerDeparturePlate = prevLoc.trailerDeparturePlate;

      // DRIVER 1 arrival/departure
      if (out.driverIdArrival == null && prevLoc?.driverIdArrival != null) out.driverIdArrival = prevLoc.driverIdArrival;
      if (!out.driverNameArrival && prevLoc?.driverNameArrival) out.driverNameArrival = prevLoc.driverNameArrival;

      if (out.driverIdDeparture == null && prevLoc?.driverIdDeparture != null) out.driverIdDeparture = prevLoc.driverIdDeparture;
      if (!out.driverNameDeparture && prevLoc?.driverNameDeparture) out.driverNameDeparture = prevLoc.driverNameDeparture;

      // DRIVER 2 arrival/departure
      if (out.driver2IdArrival == null && prevLoc?.driver2IdArrival != null) out.driver2IdArrival = prevLoc.driver2IdArrival;
      if (!out.driver2NameArrival && prevLoc?.driver2NameArrival) out.driver2NameArrival = prevLoc.driver2NameArrival;

      if (out.driver2IdDeparture == null && prevLoc?.driver2IdDeparture != null) out.driver2IdDeparture = prevLoc.driver2IdDeparture;
      if (!out.driver2NameDeparture && prevLoc?.driver2NameDeparture) out.driver2NameDeparture = prevLoc.driver2NameDeparture;

      return out;
    };

    // Mantém todos os campos que a resposta compacta do reorder não devolve
    // (coordenadas, morada, load enriquecida, etc.). Isto evita que o mapa
    // perca pontos enquanto aguarda o refresh por socket.
    const mergeReorderRowsWithSnapshot = (rows, snapshot) => {
      const safeRows = Array.isArray(rows) ? rows : [];
      const safeSnapshot = Array.isArray(snapshot) ? snapshot : [];

      return safeRows.map((row) => {
        const prevLoc =
          safeSnapshot.find(
            (point) => Number(point?.id) === Number(row?.id)
          ) || null;

        const loadFallback =
          loadsByIdRef.current?.[String(row?.loadId)] ||
          prevLoc?.load ||
          null;

        const merged = mergeStrategicFromPrev(
          {
            ...prevLoc,
            ...row,
          },
          prevLoc
        );

        return normalizeExternalDisplay({
          ...merged,
          load: merged?.load ?? loadFallback,
        });
      });
    };

    const fetchLocations = async () => {
      const refreshId = ++latestRefreshId.current;
      if (isReordering.current) return;
      if (!transportsessionId) return;

      const until = reorderCooldownUntil.get(transportsessionId) || 0;

      const doFetch = async () => {
        const response = await axios.get(
          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
        );
        if (refreshId !== latestRefreshId.current) return;

        if (isReordering.current) {
          queuedWsRefresh.current = true;
          return;
        }

        const rows = Array.isArray(response.data) ? response.data : [];
        const prevSnap = locationsRef.current || [];

        const normalized = rows.map((r) => {
          const loadFallback = loadsByIdRef.current?.[String(r?.loadId)] || null;

          const prevLoc = prevSnap.find((p) => Number(p?.id) === Number(r?.id)) || null;
          const merged = mergeStrategicFromPrev(r, prevLoc);

          return normalizeExternalDisplay({
            ...merged,
            load: merged?.load ?? loadFallback,
          });
        });
        const sorted = sortLocationsSafe(normalized);

        setExistingDateHours(sorted.map((loc) => `${loc.date}T${loc.hour}`));
        setLocations(sorted);
        onPointsChanged?.(sorted);
      };

      try {
        await doFetch();
        if (Date.now() < until) {
          setTimeout(() => {
            doFetch().catch(() => {});
          }, 400);
        }
      } catch (error) {
        console.error("❌ Error fetching pickup/delivery locations:", error);
      }
    };

    // NEW CODE BITES
    useEffect(() => {
      // mantém um map trailerId -> plate para o timeline render
      axios
        .get("/pickupdeliveries/trailers/with-latest-assignments", { params: { _: Date.now() } })
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : [];
          const map = {};
          for (const tr of rows) {
            const plate = tr?.trailerPlate || tr?.trailerNumber || null;
            if (tr?.id && plate) map[String(tr.id)] = plate;
          }
          setTrailersById(map);
        })
        .catch(() => {});
    }, []);

    // Resolve missing truck and trailer plates
    useEffect(() => {
      const requestPlate = (kind, id, plate, cache, setCache) => {
        if (id == null || String(plate || "").trim()) return;
        if (cache[String(id)]) return;

        const key = `${kind}:${id}`;

        if (plateRequestsInFlight.current.has(key)) return;

        plateRequestsInFlight.current.add(key);

        axios
          .get(`/${kind}/${id}`)
          .then(({ data }) => {
            const vehicle = data?.truck ?? data?.trailer ?? data;

            const resolvedPlate = String(
              vehicle?.plate ??
              vehicle?.truckPlate ??
              vehicle?.trailerPlate ??
              vehicle?.trailerNumber ??
              ""
            ).trim();

            if (resolvedPlate) {
              setCache((prev) => ({
                ...prev,
                [String(id)]: resolvedPlate,
              }));
            }
          })
          .catch(() => {})
          .finally(() => {
            plateRequestsInFlight.current.delete(key);
          });
      };

      locations.forEach((location) => {
        if (location?.type === "STRATEGICPOINT") return;

        requestPlate(
          "trucks",
          location.truckId,
          location.truckPlate,
          trucksById,
          setTrucksById
        );

        requestPlate(
          "trailers",
          location.trailerId,
          location.trailerPlate,
          trailersById,
          setTrailersById
        );
      });
    }, [locations, trucksById, trailersById]);

    useEffect(() => {
      locationsRef.current = locations;
    }, [locations]);

    useEffect(() => {
      setSessionMotionReady(false);

      let raf1 = null;
      let raf2 = null;

      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          setSessionMotionReady(true);
        });
      });

      return () => {
        if (raf1) cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }, [transportsessionId]);

    useEffect(() => {
      fetchLocations();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportsessionId]);

    const photoPointIds = locations
      .filter((point) =>
        point?.id != null &&
        ["PICKUP", "DELIVERY"].includes(
          String(point?.type || "").toUpperCase()
        )
      )
      .map((point) => String(point.id))
      .sort()
      .join("|");

    useEffect(() => {
      let active = true;

      const ids = photoPointIds
        ? photoPointIds.split("|")
        : [];

      setPhotoPresenceById({});

      if (ids.length > 0) {
        Promise.all(
          ids.map(async (id) => {
            try {
              const hasPhotos = await hasStoredDocPhotos(id);
              return [id, hasPhotos];
            } catch (error) {
              console.error(
                `[TSL] Failed to check photos for point ${id}:`,
                error
              );
              return [id, false];
            }
          })
        ).then((results) => {
          if (!active) return;

          setPhotoPresenceById(
            Object.fromEntries(results)
          );
        });
      }

      return () => {
        active = false;
      };
    }, [photoPointIds, transportsessionId]);

    // 🔌 WS: live updates for this transport session (auth-safe)
    useEffect(() => {
      if (!transportsessionId) return;
      const s = getSocket();

      s.emit("subscribe:session", { id: transportsessionId });

      const refreshNow = () => safeDefer(() => softRefresh(), 120);
      const onCreated = () => refreshNow();
      const onUpdated = () => refreshNow();
      const onReordered = () => refreshNow();
      const onDeleted = () => refreshNow();
      const onStatus = () => refreshNow();
      const onStale = () => refreshNow();

      s.on("pd:created", onCreated);
      s.on("pd:updated", onUpdated);
      s.on("pd:reordered", onReordered);
      s.on("pd:deleted", onDeleted);
      s.on("pd:status", onStatus);
      s.on("session:points:stale", onStale);

      return () => {
        s.emit("unsubscribe:session", { id: transportsessionId });
        s.off("pd:created", onCreated);
        s.off("pd:updated", onUpdated);
        s.off("pd:reordered", onReordered);
        s.off("pd:deleted", onDeleted);
        s.off("pd:status", onStatus);
        s.off("session:points:stale", onStale);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportsessionId]);

useEffect(() => {
  function onTruckAssigned(e) {
    const { pickupDeliveryId, truck, truckRole } = e.detail || {};
    if (!pickupDeliveryId || !truck) return;

    const clicked = (locationsRef.current || []).find((l) => Number(l.id) === Number(pickupDeliveryId));
    if (!clicked || isAssignmentBlockedNoAlert(clicked)) return;

    setLocations((prev) =>
      prev.map((loc) => {
        if (Number(loc.id) !== Number(pickupDeliveryId)) return loc;

        const plate = truck?.plate ?? loc.truckPlate ?? null;

        if (String(loc.type || "").toUpperCase() === "STRATEGICPOINT") {
          if (truckRole === "arrival") {
            return {
              ...loc,
              truckIdArrival: truck?.id ?? null,
              truckArrivalPlate: plate,
            };
          }

          if (truckRole === "departure") {
            return {
              ...loc,
              truckIdDeparture: truck?.id ?? null,
              truckDeparturePlate: plate,
            };
          }

          return loc;
        }

        return {
          ...loc,
          truckId: truck?.id ?? null,
          truckPlate: plate,
        };
      })
    );

    setResetKey((k) => k + 1);
    setTimeout(() => softRefresh(), 150);
  }

  window.addEventListener("truck-assigned", onTruckAssigned);
  return () => window.removeEventListener("truck-assigned", onTruckAssigned);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


    // Display only the company's Load number.
    // The database ID remains reserved for API calls and relations.
    const getCompanyLoadNumber = (point) => {
      const matchingLoad = (loads || []).find(
        (load) => String(load?.id) === String(point?.loadId)
      );

      const companyNumber =
        matchingLoad?.loadNumber ??
        point?.load?.loadNumber ??
        null;

      return companyNumber != null &&
        String(companyNumber).trim() !== ""
        ? String(companyNumber)
        : "—";
    };

    // ✅ Only freeze strategic points when the WHOLE session is closed
    const effectiveFreezeStrategicPoints =
      !!freezeStrategicPoints &&
      locations.length > 0 &&
      locations.every((l) => CLOSED_STATUSES.includes(l.status));

    const locationsWithLock = (() => {
      const base = sortLocationsSafe(draftOrder || locations); // order by sequence
      const lockedLocations = [];

      const closedIndices = base
        .map((loc, index) => (CLOSED_STATUSES.includes(loc.status) ? index : -1))
        .filter((index) => index !== -1);

      const lastClosedIndex = closedIndices[closedIndices.length - 1] ?? -1;
      const lastTimelineId = base.length ? base[base.length - 1].id : null;

      let canEditNextPending = true;

      base.forEach((loc, index) => {
        // Strategic points: follow timeline-lock rules for STATUS;
        // but FREEZE only if the session is fully closed.
        if (loc.type === "STRATEGICPOINT") {
          const isClosed = CLOSED_STATUSES.includes(loc.status);

          let isLocked = false;

          if (isClosed && closedIndices.length > 1) {
            isLocked = index !== lastClosedIndex;
          } else if (!isClosed) {
            if (index > lastClosedIndex && canEditNextPending) {
              isLocked = false;
              canEditNextPending = false;
            } else {
              isLocked = true;
            }
          } else {
            isLocked = false;
          }

          const shouldFreezeStrategic =
            effectiveFreezeStrategicPoints && loc.id !== lastTimelineId;

          lockedLocations.push({
            ...loc,
            isStatusLocked: isLocked,
            isFrozen: shouldFreezeStrategic,
            isLocked: false,
          });
          return;
        }

        const isFrozen = loc.loadId && frozenLoadIdsForSession.includes(loc.loadId);
        if (isFrozen) {
          lockedLocations.push({ ...loc, isLocked: true, isFrozen: true });
          return;
        }

        const isClosed = CLOSED_STATUSES.includes(loc.status);

        if (isClosed && closedIndices.length > 1) {
          lockedLocations.push({ ...loc, isLocked: index !== lastClosedIndex });
          return;
        }

        if (!isClosed) {
          if (index > lastClosedIndex && canEditNextPending) {
            lockedLocations.push({ ...loc, isLocked: false });
            canEditNextPending = false;
          } else {
            lockedLocations.push({ ...loc, isLocked: true });
          }
          return;
        }

        lockedLocations.push({ ...loc, isLocked: false });
      });
      return lockedLocations;
    })();

    const updatePickupTrucksForLoad = (loadId, truck) => {
      setLocations((prev) =>
        prev.map((loc) =>
          loc.loadId === loadId && loc.type === "PICKUP"
            ? { ...loc, truckId: truck.id, truckPlate: truck.plate ?? null }
            : loc
        )
      );
      setResetKey((prev) => prev + 1);

      setTimeout(() => {
        fetchLocations().then(() => setResetKey((prev) => prev + 1));
      }, 300);
    };

    const updateDeliveryTrucksForLoad = (loadId, truck) => {
      setLocations((prev) =>
        prev.map((loc) =>
          loc.loadId === loadId && loc.type === "DELIVERY"
            ? { ...loc, truckId: truck.id, truckPlate: truck.plate ?? null }
            : loc
        )
      );
      setResetKey((prev) => prev + 1);

      setTimeout(() => {
        fetchLocations().then(() => setResetKey((prev) => prev + 1));
      }, 300);
    };

    useImperativeHandle(ref, () => ({
      refreshLocations: fetchLocations,
      updatePickupTrucksForLoad,
      updateDeliveryTrucksForLoad,
    }));

    /* ------------------------------ Inventory finalize ------------------------------ */
    const finalizeAfterInventory = async (loadId) => {
      if (!loadId) return;

      // ✅ UI instantâneo: fecha contexto e remove já do Current Loads
      try {
        setSelectedLoadForCmr(loadId);

        // remove “pendentes” locais já
        if (setPendingCmrUploads) {
          setPendingCmrUploads((prev) => {
            const next = new Set(prev);
            next.delete(loadId);
            return next;
          });
        }

        // ✅ Emula o socket "loads:deleted" IMEDIATAMENTE (UI some já)
        // (Loads.js pode ouvir isto e remover do state)
        try {
          window.dispatchEvent(new CustomEvent("loads:deleted:optimistic", { detail: { id: loadId } }));
        } catch {}

        // ✅ Fecha modais para não ficar “a rodar”
        setInventoryModalOpen(false);
        setTrailerInventoryModalOpen(false);
        setCmrLoadModalOpen(false);

        // ✅ Agora sim: chama o backend
        await axios.post(`/loads/${loadId}/finalize-cmr`);

        // Confirmações leves (não bloqueia UX)
        setTimeout(() => {
          fetchLocations();
          if (refreshLoads) refreshLoads();
        }, 50);

        onCmrFinalized?.(loadId);
      } catch (err) {
        console.error("❌ Failed to finalize after inventory:", err);

        // ❗fallback: se falhar, faz refresh para “repor” o que removeste
        try {
          if (refreshLoads) await refreshLoads();
          await fetchLocations();
        } catch {}

        alert(tl("alerts.cmrUploadFinalizeFailed", "❌ Failed to upload/finalize CMR."));
      } finally {
        setSelectedLoadForCmr(null);
      }
    };

    /* ------------------------------ Modals routing ------------------------------ */
    const openTruckAssignUI = async (loc, truckRole) => {
      if (!loc || guardAssignmentAction(loc)) {
        dbgTruck("[TSL][openTruckAssignUI] blocked (missing loc / frozen / closed)", { loc });
        return;
      }

      dbgTruck("[TSL][openTruckAssignUI] start", {
        transportsessionId,
        pdId: loc?.id,
        loadId: loc?.loadId,
        type: loc?.type,
        truckRole,
      });

      // ✅ Strategic points: always internal selector
      if (loc.type === "STRATEGICPOINT") {
        const repLoad = getRepresentativeLoadForSession(loads, transportsessionId);

        setSelectedPD({
          ...loc,
          truckRole: truckRole || "arrival",
          load: repLoad || null, // ✅ CRITICAL: dá contexto ao modal
          isExternalCarrier: repLoad ? isExternalTransportCarrier(repLoad) : false,
        });

        setTruckModalOpen(true);
        return;
      }

      let load = (loads || []).find((l) => Number(l.id) === Number(loc.loadId));

      // ✅ Fallback: if loads[] doesn't include it, fetch the load so we can route correctly
      if (!load && loc?.loadId) {
        try {
          dbgTruck("[TSL][openTruckAssignUI] load not in props -> fetching /loads/:id", {
            loadId: loc.loadId,
          });

          const res = await axios.get(`/loads/${Number(loc.loadId)}`, {
            validateStatus: () => true,
          });

          if (res?.status === 200) {
            load = res.data?.load ?? res.data?.row ?? res.data ?? null;
          }

          dbgTruck("[TSL][openTruckAssignUI] fetched load result", {
            ok: res?.status,
            hasLoad: !!load,
          });
        } catch (e) {
          dbgTruck("[TSL][openTruckAssignUI] fetch load failed", e);
        }
      }

      // ✅ IMPORTANT: In TransportSessionLocations we ALWAYS use TruckSelectorModal
      // (both internal and subcontracted loads)
      if (load && isExternalTransportCarrier(load)) {
        dbgTruck("[TSL][openTruckAssignUI] external -> TruckSelectorModal (forced)", {
          pdId: loc?.id,
          loadId: loc?.loadId,
          type: loc?.type,
        });
        // no return; continue to open the normal selector
      }

      dbgTruck("[TSL][openTruckAssignUI] internal -> TruckSelectorModal", { pdId: loc?.id });
      setSelectedPD({
        ...loc,
        load: load || loc.load || null, // ✅ keep resolved load for modal props
        truckRole: truckRole || loc.type.toLowerCase(),
      });
      setTruckModalOpen(true);
    };

    const openDriverAssignUI = async (loc, driverRole, driverSlot = 1) => {
      if (!loc || guardAssignmentAction(loc)) {
        dbgDriver("[TSL][openDriverAssignUI] blocked (missing loc / frozen / closed)", { loc });
        return;
      }
    
      dbgDriver("[TSL][openDriverAssignUI] CLICK -> routing driver modal", {
        file: "src/components/TransportSessionLocations.js",
        transportsessionId,
        pdId: loc?.id,
        loadId: loc?.loadId,
        type: loc?.type,
        driverRole,
        driverSlot,
      });
      if (localStorage.getItem("debugDriverRouting") === "1") {
        console.trace("[TSL][openDriverAssignUI] stack");
      }
    
      // ✅ Strategic points: mantém selector normal (interno)
      if (loc.type === "STRATEGICPOINT") {
        const repLoad = getRepresentativeLoadForSession(loads, transportsessionId);

        setSelectedPDForDriver({
          ...loc,
          driverRole: driverRole || "arrival",
          load: repLoad || null,
          isExternalCarrier: repLoad ? isExternalTransportCarrier(repLoad) : false,
        });

        setSelectedDriverSlot(driverSlot);
        setDriverModalOpen(true);
        return;
      }
    
      let load = (loads || []).find((l) => Number(l.id) === Number(loc.loadId));
    
      // fallback: se não tiveres o load no props, vai buscar
      if (!load && loc?.loadId) {
        try {
          dbgDriver("[TSL][openDriverAssignUI] load not in props -> fetching /loads/:id", {
            loadId: loc.loadId,
          });
    
          const res = await axios.get(`/loads/${Number(loc.loadId)}`, { validateStatus: () => true });
          if (res?.status === 200) load = res.data?.load ?? res.data?.row ?? res.data ?? null;
    
          dbgDriver("[TSL][openDriverAssignUI] fetched load result", {
            ok: res?.status,
            hasLoad: !!load,
          });
        } catch (e) {
          dbgDriver("[TSL][openDriverAssignUI] fetch load failed", e);
        }
      }
    
      // ✅ Carrier externo -> free-text driver modal
      // ✅ IMPORTANT: In TransportSessionLocations we ALWAYS use DriverSelectorModal
      // (both internal and subcontracted loads)
      if (load && isExternalTransportCarrier(load)) {
        dbgDriver("[TSL][openDriverAssignUI] external -> DriverSelectorModal (forced)", {
          pdId: loc?.id,
          loadId: loc?.loadId,
          type: loc?.type,
        });
        // no return; continue to open the normal selector
      }
    
      // ✅ interno -> DriverSelectorModal
      dbgDriver("[TSL][openDriverAssignUI] internal -> DriverSelectorModal", {
        pdId: loc?.id,
        driverRole,
      });
    
      setSelectedPDForDriver({
        ...loc,
        load: load || loc.load || null, // ✅ keep resolved load for modal props
        driverRole,
      });
      setSelectedDriverSlot(driverSlot);
      setDriverModalOpen(true);
    };
    
    const openTrailerAssignUI = async (loc, trailerRole) => {
      if (!loc || guardAssignmentAction(loc)) return;

      // ✅ Strategic points: open trailer selector with role (arrival/departure)
      if (String(loc.type || "").toUpperCase() === "STRATEGICPOINT") {
        const repLoad = getRepresentativeLoadForSession(loads, transportsessionId);

        setSelectedPDForTrailer({
          ...loc,
          trailerRole: trailerRole || "arrival",
          load: repLoad || null, // ✅
          isExternalCarrier: repLoad ? isExternalTransportCarrier(repLoad) : false,
        });

        setTrailerModalOpen(true);
        return;
      }

      let load = (loads || []).find((l) => Number(l.id) === Number(loc.loadId));

      // fallback: fetch load if missing
      if (!load && loc?.loadId) {
        try {
          const res = await axios.get(`/loads/${Number(loc.loadId)}`, { validateStatus: () => true });
          if (res?.status === 200) load = res.data?.load ?? res.data?.row ?? res.data ?? null;
        } catch {}
      }

      const enriched = { ...loc, load: load || loc.load || null };
      setSelectedPDForTrailer(enriched);
      setTrailerModalOpen(true);
    };

    /* ------------------------ Reorder V2: React-owned state ------------------------ */

    // Compare scheduled local wall-clock dates/times.
    // Driving times and time-zone differences are not included in Phase 1.
    const scheduleToMinutes = (date, hour) => {
      const d = String(date || "").slice(0, 10);
      const h = String(hour || "").slice(0, 5);

      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(d) ||
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(h)
      ) {
        return null;
      }

      const timestamp = Date.parse(`${d}T${h}:00Z`);

      return Number.isFinite(timestamp)
        ? timestamp / 60000
        : null;
    };

    const getScheduleBounds = (point) => {
      const date = String(point?.date || "").slice(0, 10);
      const hour = String(point?.hour || "").slice(0, 5);

      const mode = String(
        point?.scheduleType || "FIXED"
      ).trim().toUpperCase();

      const isWindow = mode === "WINDOW";

      if (!["FIXED", "WINDOW"].includes(mode)) {
        return {
          earliest: null,
          latest: null,
          isWindow: false,
          invalid: true,
        };
      }

      if (!isWindow && !date) {
        return null;
      }

      const earliest = scheduleToMinutes(
        date,
        hour || (isWindow ? "" : "00:00")
      );

      const latest = scheduleToMinutes(
        date,
        isWindow
          ? point?.hourEnd
          : hour || "23:59"
      );

      return {
        earliest,
        latest,
        isWindow,
        invalid:
          earliest == null ||
          latest == null ||
          latest < earliest,
      };
    };

    // Validate the entire proposed sequence, not only adjacent hours.
    const getScheduleConflict = (points) => {
      let earliestPossible = -Infinity;
      let blockingIndex = -1;

      for (let index = 0; index < points.length; index += 1) {
        const bounds = getScheduleBounds(points[index]);

        if (!bounds) continue;

        if (bounds.invalid) {
          return {
            index,
            blockingIndex: Math.max(0, index - 1),
            reason: "INVALID_WINDOW",
          };
        }

        // A stop can begin no earlier than its own opening time
        // or the earliest feasible time of previous stops.
        if (bounds.earliest > earliestPossible) {
          earliestPossible = bounds.earliest;
          blockingIndex = index;
        }

        // The chosen start must remain within this stop's window.
        if (earliestPossible > bounds.latest) {
          return {
            index,
            blockingIndex:
              blockingIndex === index
                ? Math.max(0, index - 1)
                : blockingIndex,
            reason: "CHRONOLOGICAL_CONFLICT",
          };
        }
      }

      return null;
    };

    const isReorderLocked = (point) => {
      if (!point) return true;

      if (
        CLOSED_STATUSES.includes(
          String(point.status || "").toUpperCase()
        )
      ) {
        return true;
      }

      if (point.isFrozen === true) return true;

      if (
        point.loadId != null &&
        frozenLoadIdsForSession.some(
          (id) => String(id) === String(point.loadId)
        )
      ) {
        return true;
      }

      return (
        String(point.type || "").toUpperCase() === "STRATEGICPOINT" &&
        effectiveFreezeStrategicPoints
      );
    };

    const getSnapshotSignature = (points) =>
      sortLocationsSafe(points)
        .map((point) =>
          [
            String(point.id),
            String(point.sequence ?? ""),
            String(point.date || "").slice(0, 10),
            String(point.hour || "").slice(0, 5),
            String(point.scheduleType || "").toUpperCase(),
            String(point.hourEnd || "").slice(0, 5),
            String(point.status || "").toUpperCase(),
          ].join(":")
        )
        .join("|");

    const ensureScheduleFields = async (points) => {
      return Promise.all(
        (points || []).map(async (point) => {
          const mode = String(
            point?.scheduleType || ""
          ).toUpperCase();

          const needsDetails =
            !["FIXED", "WINDOW"].includes(mode) ||
            (mode === "WINDOW" && !point?.hourEnd);

          if (!needsDetails) return point;

          const { data } = await axios.get(
            `/pickupdeliveries/by-id/${point.id}`
          );

          const full =
            data?.pickupDelivery ??
            data?.pd ??
            data?.row ??
            data;

          const resolvedMode = String(
            full?.scheduleType || ""
          ).toUpperCase();

          if (!["FIXED", "WINDOW"].includes(resolvedMode)) {
            throw new Error(
              `Missing scheduleType for point ${point.id}`
            );
          }

          const hourEnd =
            full?.hourEnd ??
            point?.hourEnd ??
            null;

          if (resolvedMode === "WINDOW" && !hourEnd) {
            throw new Error(
              `Missing hourEnd for point ${point.id}`
            );
          }

          return {
            ...point,
            scheduleType: resolvedMode,
            hourEnd,
          };
        })
      );
    };

    const persistReorder = async (newOrder, expectedSnapshot = null) => {
      const previousSnapshot = [...locationsRef.current];

      const optimistic = newOrder.map((point, index) => ({
        ...point,
        sequence: index + 1,
        newSequence: index + 1,
      }));

      try {
        setIsLoading(true);

        // Detect changes made by another Dispatcher before saving.
        const { data: live } = await axios.get(
          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
        );

        if (!Array.isArray(live)) {
          throw new Error("REORDER_STALE_SNAPSHOT");
        }

        const liveWithSchedules = await ensureScheduleFields(live);

        if (
          getSnapshotSignature(liveWithSchedules) !==
          getSnapshotSignature(expectedSnapshot || previousSnapshot)
        ) {
          throw new Error("REORDER_STALE_SNAPSHOT");
        }

        if (getScheduleConflict(newOrder)) {
          throw new Error("REORDER_SCHEDULE_CONFLICT");
        }

        // Optimistic update, controlled entirely by React.
        setLocations(optimistic);
        onPointsChanged?.(optimistic);

        const previousById = new Map(
          previousSnapshot.map((point) => [
            String(point.id),
            point,
          ])
        );

        const updatedSequence = optimistic.map((point, index) => {
          const previous = previousById.get(String(point.id));

          const update = {
            id: point.id,
            newSequence: index + 1,
          };

          const oldDate = String(previous?.date || "").slice(0, 10);
          const newDate = String(point.date || "").slice(0, 10);

          const oldHour = String(previous?.hour || "").slice(0, 5);
          const newHour = String(point.hour || "").slice(0, 5);

          if (oldDate !== newDate) {
            update.date = newDate || null;
          }

          if (oldHour !== newHour) {
            update.hour = newHour || null;
          }

          const oldMode = String(previous?.scheduleType || "FIXED").toUpperCase();
          const newMode = String(point?.scheduleType || "FIXED").toUpperCase();

          const oldEnd = String(previous?.hourEnd || "").slice(0, 5);
          const newEnd = String(point?.hourEnd || "").slice(0, 5);

          if (oldMode !== newMode) {
            update.scheduleType = newMode;
          }

          if (oldEnd !== newEnd) {
            update.hourEnd = newEnd || null;
          }

          return update;
        });

        const { data } = await axios.put(
          "/pickupdeliveries/reorder",
          {
            transportsessionId,
            updatedSequence,
          }
        );

        let syncedRows;

        if (Array.isArray(data?.rows)) {
          syncedRows = mergeReorderRowsWithSnapshot(
            data.rows,
            optimistic
          );
        } else {
          const response = await axios.get(
            `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
          );

          syncedRows = mergeReorderRowsWithSnapshot(
            response.data,
            optimistic
          );
        }

        const sorted = sortLocationsSafe(syncedRows);

        setLocations(sorted);
        onPointsChanged?.(sorted);
        refreshLoads?.();

        return true;
      } catch (error) {
        console.error("[LOADEvo][Reorder V2] Failed:", error);

        setLocations(previousSnapshot);
        onPointsChanged?.(previousSnapshot);

        queuedWsRefresh.current = true;

        alert(
          error?.message === "REORDER_STALE_SNAPSHOT"
            ? "A sessão foi alterada entretanto. Os dados serão atualizados; volta a tentar."
            : tl(
                "alerts.reorderFailed",
                "Não foi possível guardar a nova ordem."
              )
        );

        return false;
      } finally {
        setIsLoading(false);
        releaseReorder();
      }
    };

    const proposeReorder = async (
      orderedIds,
      draggedId,
      onResolved
    ) => {
      const reject = (message) => {
        if (message) alert(message);

        releaseReorder();
        onResolved?.(false);
      };

      let previous;

      try {
        previous = await ensureScheduleFields(
          sortLocationsSafe(locationsRef.current)
        );
      } catch (error) {
        const status = error?.response?.status;
        const apiMessage =
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          error?.message ||
          "Erro desconhecido";

        console.error("[LOADEvo][Reorder] Schedule lookup failed:", {
          transportsessionId,
          status: status ?? null,
          message: apiMessage,
          error,
        });

        reject(
          "Não foi possível confirmar os horários dos pontos.\n\n" +
          `Detalhes: ${status ? `HTTP ${status} — ` : ""}${apiMessage}\n\n` +
          "A ordem não foi alterada."
        );
        return;
      }

      const invalidPoint = previous.find((point) => {
        const bounds = getScheduleBounds(point);
        return bounds?.invalid === true;
      });

      if (invalidPoint) {
        reject(
          `O ponto "${invalidPoint.location || invalidPoint.id}" ` +
          "tem um horário incompleto ou um intervalo inválido. " +
          "Corrige-o antes de reordenar."
        );
        return;
      }

      const beforeIds = previous.map(
        (point) => String(point.id)
      );

      const ids = (orderedIds || []).map(String);

      // Validate all IDs before applying any changes.
      if (
        ids.length !== beforeIds.length ||
        new Set(ids).size !== ids.length ||
        ids.some((id) => !beforeIds.includes(id))
      ) {
        reject(
          tl(
            "alerts.dragCancelledRefreshed",
            "Os pontos foram atualizados. Tenta novamente."
          )
        );
        return;
      }

      const dragged = previous.find(
        (point) =>
          String(point.id) === String(draggedId)
      );

      if (isReorderLocked(dragged)) {
        reject("Este ponto está bloqueado.");
        return;
      }

      // Closed/frozen points must retain their positions.
      if (
        previous.some(
          (point, index) =>
            isReorderLocked(point) &&
            ids[index] !== String(point.id)
        )
      ) {
        reject("Não podes deslocar pontos bloqueados.");
        return;
      }

      const byId = new Map(
        previous.map((point) => [
          String(point.id),
          point,
        ])
      );

      const proposed = ids.map((id, index) => ({
        ...byId.get(id),
        sequence: index + 1,
        newSequence: index + 1,
      }));

      const conflict = getScheduleConflict(proposed);

      if (conflict) {
        const candidates = [
          conflict.blockingIndex,
          conflict.index,
        ];

        const draggedIndex = ids.indexOf(
          String(draggedId)
        );

        const editIndex =
          candidates.includes(draggedIndex) &&
          !isReorderLocked(proposed[draggedIndex])
            ? draggedIndex
            : candidates.find(
                (index) =>
                  !isReorderLocked(proposed[index])
              );

        if (editIndex == null) {
          reject(
            "Os dois pontos em conflito estão bloqueados."
          );
          return;
        }

        // Keep the new order as an unsaved draft.
        setDraftOrder(proposed);

        // Prevent overlapping map and schedule modals.
        if (onResolved) {
          setMapModalOpen(false);
        }

        setModalData({
          draggedItem: dragged,
          newOrder: proposed,
          previousSnapshot: previous,
          newIndex: draggedIndex,
          editIndex,
          editDate: proposed[editIndex]?.date || "",
          editHour: proposed[editIndex]?.hour || "",
          editScheduleType: proposed[editIndex]?.scheduleType || "FIXED",
          editHourEnd: proposed[editIndex]?.hourEnd || "",
          resolveReorder: onResolved,
          error: "",
        });

        setShowModal(true);
        return;
      }

      const saved = await persistReorder(proposed, previous);
      onResolved?.(saved);
    };

    const onTimelineDragEnd = ({ active, over }) => {
      setDragInProgress(false);

      if (
        !over ||
        String(active.id) === String(over.id)
      ) {
        releaseReorder();
        return;
      }

      const points = sortLocationsSafe(
        locationsRef.current
      );

      const from = points.findIndex(
        (point) =>
          String(point.id) === String(active.id)
      );

      const to = points.findIndex(
        (point) =>
          String(point.id) === String(over.id)
      );

      if (
        from < 0 ||
        to < 0 ||
        from === to
      ) {
        releaseReorder();
        return;
      }
      const moved = arrayMove(
        points,
        from,
        to
      );

      // Show the proposed order immediately, before API validation.
      // Each point retains its original identity and all its fields.
      const immediateOrder = moved.map((point, index) => ({
        ...point,
        sequence: index + 1,
        newSequence: index + 1,
      }));

      setDraftOrder(immediateOrder);

      // Keep the original validation and persistence workflow.
      void proposeReorder(
        immediateOrder.map((point) => String(point.id)),
        active.id
      );
    };

    const handleMapReorder = ({
      orderedIds,
      draggedId,
    }) => {
      if (isReordering.current || isLoading) {
        return Promise.resolve(false);
      }

      isReordering.current = true;

      return new Promise((resolve) => {
        void proposeReorder(
          orderedIds,
          draggedId,
          (saved) => {
            setMapModalOpen(true);
            resolve(saved);
          }
        );
      });
    };

    const handleDateModalConfirm = async (
      newDate,
      newHour,
      newScheduleType,
      newHourEnd
    ) => {
      if (!modalData || isLoading) return;

      const editIndex = modalData.editIndex ?? modalData.newIndex;
      const original = modalData.newOrder?.[editIndex];

      if (!original || isReorderLocked(original)) return;

      const normalizedType = String(
        newScheduleType || "FIXED"
      ).trim().toUpperCase();

      const isWindow = normalizedType === "WINDOW";

      if (
        !["FIXED", "WINDOW"].includes(normalizedType) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(newDate || "") ||
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(newHour || "") ||
        (isWindow &&
          !/^([01]\d|2[0-3]):[0-5]\d$/.test(newHourEnd || ""))
      ) {
        setModalData((prev) => ({
          ...prev,
          error: "Insere uma data e horário válidos.",
        }));
        return;
      }

      const changed = modalData.newOrder.map((point, index) =>
        index === editIndex
          ? {
              ...point,
              date: newDate,
              hour: newHour,
              scheduleType: normalizedType,
              hourEnd: isWindow ? newHourEnd : null,
            }
          : point
      );

      const editedBounds = getScheduleBounds(changed[editIndex]);

      if (!editedBounds || editedBounds.invalid) {
        setModalData((prev) => ({
          ...prev,
          error:
            "O horário introduzido é inválido ou o início ultrapassa " +
            "o fim do intervalo.",
        }));
        return;
      }

      const hasChangedCommitment =
        String(original.date || "").slice(0, 10) !== newDate ||
        String(original.hour || "").slice(0, 5) !== newHour ||
        String(original.scheduleType || "FIXED").toUpperCase() !== normalizedType ||
        String(original.hourEnd || "").slice(0, 5) !==
          (isWindow ? newHourEnd : "");

      if (
        hasChangedCommitment &&
        !window.confirm(
          "Esta alteração modifica o horário acordado com o cliente. " +
          "Queres continuar?"
        )
      ) {
        return;
      }

      const nextConflict = getScheduleConflict(changed);

      if (nextConflict) {
        const candidates = [
          nextConflict.blockingIndex,
          nextConflict.index,
        ];

        const nextEditIndex =
          candidates.includes(editIndex) &&
          !isReorderLocked(changed[editIndex])
            ? editIndex
            : candidates.find(
                (index) =>
                  !isReorderLocked(changed[index])
              );

        if (nextEditIndex == null) {
          setModalData((prev) => ({
            ...prev,
            error:
              "Há um conflito entre pontos bloqueados. Reverte o movimento.",
          }));
          return;
        }

        setDraftOrder(changed);

        setModalData((prev) => ({
          ...prev,
          newOrder: changed,
          editIndex: nextEditIndex,
          editDate: changed[nextEditIndex].date || "",
          editHour: changed[nextEditIndex].hour || "",
          editScheduleType: changed[nextEditIndex].scheduleType || "FIXED",
          editHourEnd: changed[nextEditIndex].hourEnd || "",
          error:
            "Horário atualizado na proposta. Resolve agora o conflito restante.",
        }));
        return;
      }

      const onResolved = modalData.resolveReorder;

      const saved = await persistReorder(
        changed,
        modalData.previousSnapshot
      );

      setSessionCalendar(null);
      setSessionSchedulePopup(null);
      setShowModal(false);
      setModalData(null);

      onResolved?.(saved);
    };
    const handleCancelModal = () => {
      const onResolved = modalData?.resolveReorder;

      setSessionCalendar(null);
      setSessionSchedulePopup(null);
      setShowModal(false);
      setModalData(null);

      releaseReorder();

      onResolved?.(false);
    };

    const applyConflictTimeSchedule = ({
      scheduleType,
      hour,
      hourEnd,
    }) => {
      if (!sessionSchedulePopup) return;

      const normalizedType =
        String(scheduleType).toUpperCase() === "WINDOW"
          ? "WINDOW"
          : "FIXED";

      setModalData((previous) => {
        if (!previous) return previous;

        return {
          ...previous,
          editScheduleType: normalizedType,
          editHour: hour,
          editHourEnd:
            normalizedType === "WINDOW"
              ? hourEnd
              : null,
          error: "",
        };
      });

      setSessionSchedulePopup(null);
    };

    /* ------------------------------ Assign truck/driver ------------------------------ */
    const handleTruckAssign = async (pickupDeliveryId, truckId, type, truckRole, truckPlate) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const tUpper = String(type || "").toUpperCase();

      const selectedPlate =
        typeof truckPlate === "string" && truckPlate.trim()
          ? truckPlate.trim().toUpperCase()
          : null;

      const manualPlate = truckId == null ? selectedPlate : null;

      let optimisticPlate = selectedPlate;

      if (!optimisticPlate && truckId) {
        try {
          const tRes = await axios.get(`/trucks/${truckId}`, {
            validateStatus: () => true,
          });

          optimisticPlate = tRes?.data?.plate ?? null;
        } catch {}
      }

      if (truckId != null && optimisticPlate) {
        setTrucksById((prev) => ({
          ...prev,
          [String(truckId)]: optimisticPlate,
        }));
      }
      const prev = locationsRef.current;

      // optimistic UI
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (tUpper === "STRATEGICPOINT") {
            if (truckRole === "arrival") {
              return { ...loc, truckIdArrival: truckId ?? null, truckArrivalPlate: optimisticPlate };
            }
            if (truckRole === "departure") {
              return { ...loc, truckIdDeparture: truckId ?? null, truckDeparturePlate: optimisticPlate };
            }
            return loc;
          }

          return { ...loc, truckId: truckId ?? null, truckPlate: optimisticPlate };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (tUpper === "STRATEGICPOINT") {
          if (truckRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-arrival`, {
              truckId: truckId ?? null,
              truckPlate: manualPlate, // ✅
            });
          } else if (truckRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-departure`, {
              truckId: truckId ?? null,
              truckPlate: manualPlate, // ✅
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck`, {
            truckId: truckId ?? null,
            truckPlate: manualPlate, // ✅ (se o backend aceitar, ótimo; se ignorar, ok)
            type: String(type || "").toLowerCase(),
          });
        }

        setTimeout(() => softRefresh(), 100);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        setLocations(prev);
        setResetKey((k) => k + 1);
        console.error("❌ Truck assignment failed:", e);
        alert(tl("alerts.assignTruckFailed", "Failed to assign truck. Please try again."));
      }
    };

    const handleRemoveTruck = async (pickupDeliveryId, truckRole) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      // ✅ If pickup/delivery belongs to EXTERNAL carrier -> clear manual plate on LOAD + UI
      const clickedLoad = clicked?.loadId ? (loads || []).find((l) => l.id === clicked.loadId) : null;

      const isPD = clicked?.type === "PICKUP" || clicked?.type === "DELIVERY";
      const isExternal = clickedLoad && isExternalTransportCarrier(clickedLoad);

      // ✅ default behavior (internal trucks + strategic arrival/departure)
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (truckRole === "arrival") return { ...loc, truckIdArrival: null, truckArrivalPlate: null };
          if (truckRole === "departure") return { ...loc, truckIdDeparture: null, truckDeparturePlate: null };

          return { ...loc, truckId: null, truckPlate: null };
        })
      );
      setResetKey((prevKey) => prevKey + 1);

      setIsLoading(true);
      try {
        if (truckRole === "arrival") {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-arrival`, { 
            truckId: null,
            truckPlate: null,
          });
        } else if (truckRole === "departure") {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck-departure`, {
            truckId: null,
            truckPlate: null,
          });
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-truck`, {
            truckId: null,
            truckPlate: null,
            type: truckRole, // pickup | delivery
          });
        }

        fetchLocations();
        if (refreshLoads) refreshLoads();
      } catch (e) {
        alert(tl("alerts.unassignTruckFailed", "Failed to unassign truck."));
      }
      setIsLoading(false);
    };

    /* ------------------------------ Assign trailer ------------------------------ */
    const handleTrailerAssign = async (pickupDeliveryId, trailerId, type, trailerRole, trailerPlate) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const prev = locationsRef.current;
      const tUpper = String(type || "").toUpperCase();

      const selectedPlate =
        typeof trailerPlate === "string" && trailerPlate.trim()
          ? trailerPlate.trim().toUpperCase()
          : null;

      const manualPlate = trailerId == null ? selectedPlate : null;

      const plateFromId =
        trailerId != null
          ? trailersById[String(trailerId)] ?? null
          : null;

      const optimisticPlate = selectedPlate || plateFromId || null;

      if (trailerId != null && optimisticPlate) {
        setTrailersById((prev) => ({
          ...prev,
          [String(trailerId)]: optimisticPlate,
        }));
      }

      // optimistic
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (tUpper === "STRATEGICPOINT") {
            if (trailerRole === "arrival") {
              return { ...loc, trailerIdArrival: trailerId ?? null, trailerArrivalPlate: optimisticPlate };
            }
            if (trailerRole === "departure") {
              return { ...loc, trailerIdDeparture: trailerId ?? null, trailerDeparturePlate: optimisticPlate };
            }
            return loc;
          }

          return {
            ...loc,
            trailerId: trailerId ?? null,
            trailerPlate: optimisticPlate ?? null,
          };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (tUpper === "STRATEGICPOINT") {
          if (trailerRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-arrival`, {
              trailerId: trailerId ?? null,
              trailerPlate: manualPlate, // ✅
            });
          } else if (trailerRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-departure`, {
              trailerId: trailerId ?? null,
              trailerPlate: manualPlate, // ✅
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer`, {
            trailerId: trailerId ?? null,
            trailerPlate: manualPlate, // ✅
          });
        }

        setTimeout(() => softRefresh(), 120);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        console.error("❌ Trailer assignment failed:", e);
        setLocations(prev);
        setResetKey((k) => k + 1);
        alert(tl("alerts.assignTrailerFailed", "Failed to assign trailer. Please try again."));
      }
    };

    const handleRemoveTrailer = async (pickupDeliveryId, type, trailerRole) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const prev = locationsRef.current;
      const tUpper = String(type || "").toUpperCase();

      // optimistic
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (tUpper === "STRATEGICPOINT") {
            if (trailerRole === "arrival") return { ...loc, trailerIdArrival: null, trailerArrivalPlate: null };
            if (trailerRole === "departure") return { ...loc, trailerIdDeparture: null, trailerDeparturePlate: null };
            return loc;
          }

          return { ...loc, trailerId: null, trailerPlate: null };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (tUpper === "STRATEGICPOINT") {
          if (trailerRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-arrival`, {
              trailerId: null,
              trailerPlate: null, // ✅
            });
          } else if (trailerRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer-departure`, {
              trailerId: null,
              trailerPlate: null, // ✅
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-trailer`, {
            trailerId: null,
            trailerPlate: null, // ✅
          });
        }

        setTimeout(() => softRefresh(), 120);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        console.error("❌ Trailer unassignment failed:", e);
        setLocations(prev);
        setResetKey((k) => k + 1);
        alert(tl("alerts.unassignTrailerFailed", "Failed to unassign trailer."));
      }
    };

    const handleDriverAssign = async (
      pickupDeliveryId,
      driverId,
      driverName,
      type,
      driverRole,
      driverSlot = 1
    ) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      const prev = locationsRef.current;

      const tUpper = String(type || "").toUpperCase();
      const isStrategic = tUpper === "STRATEGICPOINT" || tUpper === "ARRIVAL" || tUpper === "DEPARTURE";

      const resolvedRole =
        driverRole ||
        (tUpper === "ARRIVAL" ? "arrival" : tUpper === "DEPARTURE" ? "departure" : undefined);

      // optimistic
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (isStrategic) {
            if (resolvedRole === "arrival") {
              return driverSlot === 2
                ? {
                    ...loc,
                    driver2IdArrival: driverId ?? null,
                    driver2NameArrival: driverName ?? null,
                    driverRole: "arrival",
                  }
                : {
                    ...loc,
                    driverIdArrival: driverId ?? null,
                    driverNameArrival: driverName ?? null,
                    driverRole: "arrival",
                  };
            }

            if (resolvedRole === "departure") {
              return driverSlot === 2
                ? {
                    ...loc,
                    driver2IdDeparture: driverId ?? null,
                    driver2NameDeparture: driverName ?? null,
                    driverRole: "departure",
                  }
                : {
                    ...loc,
                    driverIdDeparture: driverId ?? null,
                    driverNameDeparture: driverName ?? null,
                    driverRole: "departure",
                  };
            }

            return loc;
          }

          return driverSlot === 2
            ? { ...loc, driver2Id: driverId ?? null, driver2Name: driverName ?? null }
            : { ...loc, driverId: driverId ?? null, driverName: driverName ?? null };
        })
      );
      setResetKey((k) => k + 1);

      try {
        if (isStrategic) {
          if (resolvedRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-arrival`, {
              driverId,
              driverSlot,
            });
          } else if (resolvedRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-departure`, {
              driverId,
              driverSlot,
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver`, {
            driverId,
            type: String(type || "").toLowerCase(), // pickup | delivery
            driverSlot,
          });
        }

        setTimeout(() => softRefresh(), 100);
        if (refreshLoads) refreshLoads();
      } catch (e) {
        setLocations(prev);
        setResetKey((k) => k + 1);
        console.error("❌ Driver assignment failed:", e);
        alert(tl("alerts.assignDriverFailed", "Failed to assign driver. Please try again."));
      }
    };

    const handleRemoveDriver = async (pickupDeliveryId, driverRole, driverSlot = 1) => {
      const clicked = (locationsRef.current || []).find((l) => l.id === pickupDeliveryId);
      if (!clicked || guardAssignmentAction(clicked)) return;

      // ✅ If pickup/delivery belongs to EXTERNAL carrier -> clear manual name on LOAD + UI
      const clickedLoad = clicked?.loadId
        ? (loads || []).find((l) => Number(l.id) === Number(clicked.loadId))
        : null;

      const isPD = clicked?.type === "PICKUP" || clicked?.type === "DELIVERY";
      const isExternal = clickedLoad && isExternalTransportCarrier(clickedLoad);

      if (isPD && isExternal && clicked?.loadId) {
        const loadId = Number(clicked.loadId);

        const clearedPayload =
          clicked.type === "DELIVERY" ? { driverDeliveryInfo: null } : { driverPickupInfo: null };

        // optimistic: clear driver for ALL same load + same type
        setLocations((prev) =>
          prev.map((loc) => {
            if (Number(loc.loadId) !== loadId) return loc;
            if (clicked.type === "DELIVERY" && loc.type !== "DELIVERY") return loc;
            if (clicked.type === "PICKUP" && loc.type !== "PICKUP") return loc;
            return driverSlot === 2
              ? { ...loc, driver2Id: null, driver2Name: null }
              : { ...loc, driverId: null, driverName: null };
          })
        );
        setResetKey((k) => k + 1);

        try {
          await axios.put(`/loads/${loadId}`, clearedPayload);
          setTimeout(() => softRefresh(), 120);
          if (refreshLoads) refreshLoads();
        } catch (e) {
          console.error("❌ Failed to clear free-text driver:", e);
          alert(tl("alerts.unassignDriverFailed", "Failed to unassign driver."));
          setTimeout(() => softRefresh(), 150);
        }
        return;
      }

      // ✅ default behavior (internal drivers + strategic arrival/departure)
      setLocations((prevLocs) =>
        prevLocs.map((loc) => {
          if (loc.id !== pickupDeliveryId) return loc;

          if (driverRole === "arrival") {
            return driverSlot === 2
              ? { ...loc, driver2IdArrival: null, driver2NameArrival: null }
              : { ...loc, driverIdArrival: null, driverNameArrival: null };
          }

          if (driverRole === "departure") {
            return driverSlot === 2
              ? { ...loc, driver2IdDeparture: null, driver2NameDeparture: null }
              : { ...loc, driverIdDeparture: null, driverNameDeparture: null };
          }

          return driverSlot === 2
            ? { ...loc, driver2Id: null, driver2Name: null }
            : { ...loc, driverId: null, driverName: null };
        })
      );
      setResetKey((prevKey) => prevKey + 1);

      try {
          if (driverRole === "arrival") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-arrival`, {
              driverId: null,
              driverSlot,
            });
          } else if (driverRole === "departure") {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver-departure`, {
              driverId: null,
              driverSlot,
            });
          } else {
            await axios.put(`/pickupdeliveries/${pickupDeliveryId}/assign-driver`, {
              driverId: null,
              type: driverRole, // pickup | delivery
              driverSlot,
            });
          }

        fetchLocations();
        if (refreshLoads) refreshLoads();
      } catch (e) {
        alert(tl("alerts.unassignDriverFailed", "Failed to unassign driver."));
      }
    };

    /* ------------------------------ Inventory/CMR flow ------------------------------ */
    const openInventoryForLastDelivery = async (deliveryPd) => {
      try {
        const loadId = deliveryPd.loadId;
        const currentLoad = (loads || []).find((l) => Number(l.id) === Number(loadId));
        
        // ⚠️ Don't block just because status is COMPLETED/CANCELLED.
        // Real finalization is /finalize-cmr (history+ws), not status.

        setSelectedLoadForCmr(loadId);

        // ✅ NEW: External/subcontracted carrier -> SKIP inventory and finalize immediately
        if (currentLoad && isExternalTransportCarrier(currentLoad)) {
          await finalizeAfterInventory(loadId);
          return;
        }

        const trailerId =
          currentLoad?.trailerIdDelivery ??
          currentLoad?.trailerIdPickup ??
          deliveryPd?.trailerId ??
          deliveryPd?.trailer?.id ??
          null;

        if (trailerId) {
          try {
            const tRes = await axios.get(`/trailers/${trailerId}`);
            const tr = tRes.data || {};
            setTrailerMeta({ id: tr.id, trailerPlate: tr.trailerPlate, status: tr.status });
            setTrailerInvData({
              numberOfStraps: tr.numberOfStraps ?? 0,
              numberOfRatchets: tr.numberOfRatchets ?? 0,
              numberOfEdgeProtectors: tr.numberOfEdgeProtectors ?? 0,
              numberOfAntiSlipMats: tr.numberOfAntiSlipMats ?? 0,
              numberOfWoodenBeams: tr.numberOfWoodenBeams ?? 0,
              numberOfSidebars: tr.numberOfSidebars ?? 0,
              numberOfFireExtinguishers: tr.numberOfFireExtinguishers ?? 0,
            });
          } catch (e) {
            console.warn("Could not fetch trailer; will skip trailer inventory.", e);
            setTrailerMeta(null);
            setTrailerInvData(null);
          }
        } else {
          setTrailerMeta(null);
          setTrailerInvData(null);
        }

        const truckId = deliveryPd?.truckId ?? null;

        // ✅ Only inventory prompts now (no CMR modal anymore)
        if (truckId) {
          const res = await axios.get(`/trucks/${truckId}`);
          const trk = res.data || {};
          setInventoryTruck({ id: trk.id, plate: trk.plate });
          setInventoryData({
            number_of_straps: trk.number_of_straps ?? 0,
            number_of_ratchets: trk.number_of_ratchets ?? 0,
            number_of_edge_protectors: trk.number_of_edge_protectors ?? 0,
            number_of_anti_slip_mats: trk.number_of_anti_slip_mats ?? 0,
          });
          setInventoryModalOpen(true);
        } else if (trailerId) {
          setTrailerInventoryModalOpen(true);
        } else {
          // ✅ nothing to prompt -> still must finalize (move to LoadHistory)
          await finalizeAfterInventory(loadId);
          return;
        }
      } catch (e) {
        console.error("Failed to prepare inventory flow:", e);
      }
    };

    const handleDeleteStrategicPoint = async (pickupDeliveryId) => {
      const ok = window.confirm(
        tl("confirm.deleteStrategicPoint", "Are you sure you want to delete this strategic point?")
      );
      if (!ok) return;

      try {
        await axios.delete(`/pickupdeliveries/${pickupDeliveryId}`);
        fetchLocations();
        if (refreshLoads) refreshLoads();
      } catch (err) {
        alert(tl("alerts.deleteStrategicPointFailed", "❌ Failed to delete strategic point."));
        console.error("Delete error:", err);
      }
    };

    const startLastDeliveryCmrFlow = async (deliveryPd, loadId) => {
      setPendingInventoryAfterCmr({
        deliveryPd,
        loadId: Number(loadId),
      });

      setSelectedLoadForCmr(Number(loadId));
      setSelectedPdForPhotos(deliveryPd?.id ?? null);
      setCmrLoadModalOpen(true);
    };

    const continuePendingInventoryAfterCmr = async () => {
      const pending = pendingInventoryAfterCmr;

      setCmrLoadModalOpen(false);
      setSelectedPdForPhotos(null);
      setPendingInventoryAfterCmr(null);

      if (!pending?.deliveryPd || !pending?.loadId) return;

      await openInventoryForLastDelivery(pending.deliveryPd);
    };

    // ✅ status cannot move off PENDING without truck + driver (also supports free-text for external carriers)
    const isPendingStatus = (s) => String(s || "").toUpperCase() === "PENDING";

    const hasTruckAssignedForPoint = (loc) => {
      const hasId = !!loc?.truckId;
      const hasPlate = !!String(loc?.truckPlate || "").trim();
      return hasId || hasPlate;
    };

    const hasDriverAssignedForPoint = (loc) => {
      const hasId = !!loc?.driverId;
      const hasName = !!String(loc?.driverName || "").trim();
      return hasId || hasName;
    };

    const canChangeStatusOffPending = (loc) => {
      if (!loc) return false;
      const t = String(loc.type || "").toUpperCase();
      if (t === "STRATEGICPOINT") return true;
      return hasTruckAssignedForPoint(loc) && hasDriverAssignedForPoint(loc);
    };

    const statusLabel = (status) => {
      const key = String(status || "").toUpperCase();
      if (!key) return "";

      return t(`status_selector_modal:statuses.${key}`, {
        defaultValue: t(`status_selector_modal:subOptions.${key}`, {
          defaultValue: t(`status_selector_modal:subSubOptions.${key}`, {
            defaultValue: t(`status_selector_modal:subSubSubOptions.${key}`, {
              defaultValue: tl(`statuses.${key}`, key),
            }),
          }),
        }),
      });
    };

    const typeLabel = (type) => {
      const key = String(type || "").toUpperCase();
      return tl(`types.${key}`, key);
    };

    const getBlockedStatusHoverMessage = () =>
      tl(
        "tooltips.blockedStatusNeedsNextPointReverted",
        'If you want to edit the status of this point, then you should revert the next point status from "Complete/Cancelled" to "Pendent".'
      );

    const isAssignmentLocked = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();
      return location?.isFrozen || CLOSED_STATUSES.includes(statusUpper);
    };

    const getBlockedAssignmentHoverMessage = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();

      if (location?.isFrozen) {
        return tl("tooltips.assignmentLockedFrozen", "Editing is locked");
      }

      if (statusUpper === "COMPLETED" || statusUpper === "CANCELLED") {
        return tl(
          "tooltips.assignmentLockedClosedPoint",
          'This point is already "{{status}}" — truck, trailer and driver can no longer be changed.',
          { status: statusLabel(statusUpper) }
        );
      }

      return "";
    };

    const isClosedPoint = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();
      return CLOSED_STATUSES.includes(statusUpper);
    };

    const isClosedStrategicPoint = (location) => {
      const typeUpper = String(location?.type || "").toUpperCase();
      return typeUpper === "STRATEGICPOINT" && isClosedPoint(location);
    };

    const getClosedStrategicPointAssignmentMessage = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();

      return tl(
        "alerts.cannotChangeAssignmentClosedStrategicPoint",
        '❌ Este ponto estratégico já está "{{status}}" e, por isso, não pode alterar o camião, reboque ou motorista.',
        { status: statusLabel(statusUpper) }
      );
    };

    const getClosedStrategicPointEditMessage = (location) => {
      const statusUpper = String(location?.status || "").toUpperCase();

      return tl(
        "alerts.cannotEditClosedStrategicPoint",
        '❌ Este ponto estratégico já está "{{status}}" e, por isso, não pode ser editado ou apagado.',
        { status: statusLabel(statusUpper) }
      );
    };

    const guardStrategicPointEditAction = (location) => {
      if (!location) return true;

      if (isClosedStrategicPoint(location)) {
        alert(getClosedStrategicPointEditMessage(location));
        return true;
      }

      return !!location?.isFrozen;
    };

    const isAssignmentBlockedNoAlert = (location) => {
      if (!location) return true;
      return isAssignmentLocked(location);
    };

    const guardAssignmentAction = (location) => {
      if (!location) return true;

      if (isClosedStrategicPoint(location)) {
        alert(getClosedStrategicPointAssignmentMessage(location));
        return true;
      }

      return isAssignmentLocked(location);
    };

    const getAssignmentButtonDisabled = (location) => {
      if (!location) return true;

      // Para pontos estratégicos concluídos/cancelados, NÃO usamos disabled,
      // senão o clique não chega ao onClick e não conseguimos mostrar o aviso.
      if (isClosedStrategicPoint(location)) {
        return !!isLoading || !!location?.isFrozen;
      }

      return !!isLoading || isAssignmentLocked(location);
    };

    const hasAssignedTruck = (location, role = null) => {
      const typeUpper = String(location?.type || "").toUpperCase();

      if (typeUpper === "STRATEGICPOINT") {
        if (role === "arrival") {
          return !!(location?.truckIdArrival || String(location?.truckArrivalPlate || "").trim());
        }
        if (role === "departure") {
          return !!(location?.truckIdDeparture || String(location?.truckDeparturePlate || "").trim());
        }
        return false;
      }

      return !!(location?.truckId || String(location?.truckPlate || "").trim());
    };

    const hasAssignedTrailer = (location, role = null) => {
      const typeUpper = String(location?.type || "").toUpperCase();

      if (typeUpper === "STRATEGICPOINT") {
        if (role === "arrival") {
          return !!(location?.trailerIdArrival || String(location?.trailerArrivalPlate || "").trim());
        }
        if (role === "departure") {
          return !!(location?.trailerIdDeparture || String(location?.trailerDeparturePlate || "").trim());
        }
        return false;
      }

      return !!(location?.trailerId || String(location?.trailerPlate || "").trim());
    };

    const hasAssignedDriver = (location, role = null, driverSlot = 1) => {
      const typeUpper = String(location?.type || "").toUpperCase();

      if (typeUpper === "STRATEGICPOINT") {
        if (role === "arrival") {
          return driverSlot === 2
            ? !!(location?.driver2IdArrival || String(location?.driver2NameArrival || "").trim())
            : !!(location?.driverIdArrival || String(location?.driverNameArrival || "").trim());
        }

        if (role === "departure") {
          return driverSlot === 2
            ? !!(location?.driver2IdDeparture || String(location?.driver2NameDeparture || "").trim())
            : !!(location?.driverIdDeparture || String(location?.driverNameDeparture || "").trim());
        }

        return false;
      }

      return driverSlot === 2
        ? !!(location?.driver2Id || String(location?.driver2Name || "").trim())
        : !!(location?.driverId || String(location?.driverName || "").trim());
    };

    return (
      <>
        <ModalPortal>
          {showModal && modalData && (() => {
            const points = modalData.newOrder || [];

            // Identify the actual blocking stop, even when non-adjacent.
            const conflict = getScheduleConflict(points);
            if (!conflict) return null;

            const firstIndex = conflict.blockingIndex;
            const secondIndex = conflict.index;

            // Show both neighbours at the NEW position of the dragged stop.
            const foundMovedIndex = points.findIndex(
              (point) => String(point.id) === String(modalData.draggedItem?.id)
            );

            const movedIndex = foundMovedIndex >= 0
              ? foundMovedIndex
              : Math.max(0, Number(modalData.newIndex) || 0);

            const routeContext = [
              {
                key: "before",
                label: "Previous stop",
                index: movedIndex - 1,
                point: points[movedIndex - 1],
              },
              {
                key: "moved",
                label: "Moved stop",
                index: movedIndex,
                point: points[movedIndex],
              },
              {
                key: "after",
                label: "Next stop",
                index: movedIndex + 1,
                point: points[movedIndex + 1],
              },
            ];

            const selectedIndex =
              modalData.editIndex ?? modalData.newIndex;

            const selectedPoint = points[selectedIndex];
            const previousStop = points[selectedIndex - 1] || null;
            const nextStop = points[selectedIndex + 1] || null;

            const isScheduleLocked = isReorderLocked;

            const editDate =
              modalData.editDate ?? selectedPoint?.date ?? "";

            const editHour =
              modalData.editHour ?? selectedPoint?.hour ?? "";

            const editScheduleType = String(
              modalData.editScheduleType ??
              selectedPoint?.scheduleType ??
              "FIXED"
            ).trim().toUpperCase();

            const editHourEnd =
              modalData.editHourEnd ?? selectedPoint?.hourEnd ?? "";

            const isWindowEdit = editScheduleType === "WINDOW";

            const describeSchedule = (point) => {
              if (!point?.date) return "Date not scheduled";

              const date = formatDateLocal(point.date);
              const start = String(point.hour || "").slice(0, 5);
              const end = String(point.hourEnd || "").slice(0, 5);

              if (!start) return `${date} · Time not set`;

              if (
                String(point.scheduleType || "").toUpperCase() === "WINDOW" &&
                end
              ) {
                return `${date} · ${start}–${end}`;
              }

              return `${date} · ${start}`;
            };

            const candidate = {
              ...selectedPoint,
              date: editDate,
              hour: editHour,
              scheduleType: editScheduleType,
              hourEnd: isWindowEdit ? editHourEnd : null,
            };

            const candidateBounds = getScheduleBounds(candidate);

            const candidateIsComplete =
              /^\d{4}-\d{2}-\d{2}$/.test(editDate) &&
              /^([01]\d|2[0-3]):[0-5]\d$/.test(editHour) &&
              (!isWindowEdit ||
                /^([01]\d|2[0-3]):[0-5]\d$/.test(editHourEnd)) &&
              candidateBounds != null &&
              !candidateBounds.invalid;

            const previewPoints = points.map((point, index) =>
              index === selectedIndex ? candidate : point
            );

            const previewConflict = candidateIsComplete
              ? getScheduleConflict(previewPoints)
              : null;

            return (
              <div className="loadevo-session-conflict-overlay">
                <div
                  className="loadevo-session-conflict-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="loadevo-session-conflict-title"
                >
                  <div className="loadevo-session-conflict-heading">
                    <FaExclamationTriangle aria-hidden="true" />

                    <div>
                      <h3 id="loadevo-session-conflict-title">
                        Schedule conflict
                      </h3>

                      <p>
                        The proposed route order does not match the scheduled times.
                        Review the moved stop and its neighbours, then choose which time to change.
                      </p>
                    </div>
                  </div>

                  <div className="loadevo-session-route-title">
                    Proposed position
                  </div>

                  <div className="loadevo-session-route-context">
                    {routeContext.map(({ key, label, index, point }, position) => (
                      <React.Fragment key={key}>
                        {position > 0 && (
                          <div className="loadevo-session-route-then">
                            ↓ &nbsp; Then
                          </div>
                        )}

                        <div
                          className={`loadevo-session-route-stop ${
                            key === "moved" ? "is-moved" : ""
                          } ${!point ? "is-empty" : ""}`}
                        >
                          <span className="loadevo-session-route-index">
                            {point ? index + 1 : "—"}
                          </span>

                          <div className="loadevo-session-route-stop-main">
                            <small>{label}</small>

                            <strong>
                              {point?.location ||
                                (key === "before"
                                  ? "Start of route"
                                  : "End of route")}
                            </strong>

                            {point && (
                              <span>
                                {point.type === "PICKUP"
                                  ? "Pickup"
                                  : point.type === "DELIVERY"
                                    ? "Delivery"
                                    : "Strategic point"}

                                {point.city ? ` · ${point.city}` : ""}
                              </span>
                            )}
                          </div>

                          {point && (
                            <span className="loadevo-session-route-time">
                              {describeSchedule(point)}
                            </span>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>

                  <p className="loadevo-session-conflict-explanation">
                    <strong>
                      {points[firstIndex]?.location || "Previous stop"}
                    </strong>
                    {" is scheduled for "}
                    {describeSchedule(points[firstIndex])}
                    {", but appears before "}
                    <strong>
                      {points[secondIndex]?.location || "Next stop"}
                    </strong>
                    {" ("}
                    {describeSchedule(points[secondIndex])}
                    {") in the proposed order."}
                  </p>

                  <div className="loadevo-session-route-title">
                    Choose which stop to adjust
                  </div>

                  <div className="loadevo-session-conflict-choices">
                    {[firstIndex, secondIndex].map((index) => {
                      const point = points[index];
                      const locked = isScheduleLocked(point);
                      const active = selectedIndex === index;

                      return (
                        <button
                          key={point.id}
                          type="button"
                          disabled={locked || isLoading}
                          className={`loadevo-session-conflict-choice ${
                            active ? "is-selected" : ""
                          }`}
                          aria-pressed={active}
                          onClick={() => {
                            setSessionCalendar(null);
                            setSessionSchedulePopup(null);

                            setModalData((previous) => ({
                              ...previous,
                              editIndex: index,
                              editDate: point.date || "",
                              editHour: point.hour || "",
                              editScheduleType: point.scheduleType || "FIXED",
                              editHourEnd: point.hourEnd || "",
                              error: "",
                            }));
                          }}
                        >
                          <span className="loadevo-session-conflict-point-number">
                            #{index + 1}
                          </span>

                          <strong>
                            {point.location || "Unnamed location"}
                          </strong>

                          <small>
                            {point.type === "PICKUP" ? "Pickup" : "Delivery"}
                          </small>

                          <span className="loadevo-session-conflict-current-time">
                            {formatDateLocal(point.date)} · {point.hour || "00:00"}
                          </span>

                          <span className="loadevo-session-conflict-choice-hint">
                            {locked
                              ? "Locked"
                              : active
                                ? "Editing this stop"
                                : "Select to adjust"}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="loadevo-session-conflict-editor">
                    <div className="loadevo-session-conflict-editor-title">
                      <strong>Adjust date and time</strong>
                    </div>

                    <strong>
                      Editing: {selectedPoint?.location || "Unnamed location"}
                    </strong>

                    <div className="loadevo-session-conflict-fields">
                      {/* Schedule type is selected in the Create Load picker. */}

                      <div className="loadevo-session-conflict-date-field">
                        <label htmlFor="loadevo-session-edit-date">
                          Date
                        </label>

                        <div className="loadevo-session-conflict-date-picker">
                          <button
                            id="loadevo-session-edit-date"
                            type="button"
                            className="loadevo-session-date-trigger"
                            aria-haspopup="dialog"
                            aria-expanded={Boolean(sessionCalendar)}
                            disabled={isScheduleLocked(selectedPoint) || isLoading}
                            onClick={(event) => {
                              const rect =
                                event.currentTarget.getBoundingClientRect();

                              const width = Math.min(
                                350,
                                window.innerWidth - 24
                              );

                              const popupHeight = Math.min(
                                415,
                                window.innerHeight - 24
                              );

                              const left = Math.max(
                                12,
                                Math.min(
                                  rect.left,
                                  window.innerWidth - width - 12
                                )
                              );

                              const top =
                                rect.bottom + popupHeight + 8 <=
                                window.innerHeight - 12
                                  ? rect.bottom + 8
                                  : rect.top - popupHeight - 8 >= 12
                                    ? rect.top - popupHeight - 8
                                    : Math.max(
                                        12,
                                        (window.innerHeight - popupHeight) / 2
                                      );

                              setSessionSchedulePopup(null);

                              setSessionCalendar({
                                pointId: selectedPoint.id,
                                value: editDate,
                                top,
                                left,
                              });
                            }}
                          >
                            <span className="loadevo-session-date-value">
                              {editDate
                                ? formatDateLocal(editDate)
                                : "Select date"}
                            </span>

                            <span className="loadevo-session-date-icon">
                              <CalendarDays
                                size={20}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            </span>
                          </button>
                        </div>
                      </div>

                      <div className="loadevo-session-conflict-schedule-field">
                        <label>Schedule</label>

                        <button
                          type="button"
                          className="loadevo-session-conflict-schedule-launch"
                          disabled={isScheduleLocked(selectedPoint) || isLoading}
                          onClick={(event) => {
                            const rect =
                              event.currentTarget.getBoundingClientRect();

                            const width = Math.min(
                              490,
                              window.innerWidth - 24
                            );

                            const popupHeight = Math.min(
                              440,
                              window.innerHeight - 24
                            );

                            const left = Math.max(
                              12,
                              Math.min(
                                rect.left,
                                window.innerWidth - width - 12
                              )
                            );

                            const top =
                              rect.bottom + popupHeight + 8 <=
                              window.innerHeight - 12
                                ? rect.bottom + 8
                                : rect.top - popupHeight - 8 >= 12
                                  ? rect.top - popupHeight - 8
                                  : Math.max(
                                      12,
                                      (window.innerHeight - popupHeight) / 2
                                    );

                            setSessionCalendar(null);

                            setSessionSchedulePopup({
                              pointId: selectedPoint.id,
                              kind:
                                selectedPoint.type === "DELIVERY"
                                  ? "delivery"
                                  : "pickup",
                              scheduleType: editScheduleType,
                              hour: editHour,
                              hourEnd: editHourEnd,
                              top,
                              left,
                            });
                          }}
                        >
                          <span className="loadevo-session-conflict-schedule-value">
                            {editScheduleType === "WINDOW"
                              ? `${editHour || "--:--"}–${editHourEnd || "--:--"}`
                              : editHour || "Set time"}
                          </span>

                          <span className="loadevo-session-conflict-schedule-caption">
                            {editScheduleType === "WINDOW"
                              ? "Time window"
                              : "Fixed time"}
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="loadevo-session-schedule-limits">
                      <div>
                        <span>Previous stop</span>

                        <strong>
                          {previousStop
                            ? `${describeSchedule(previousStop)} (${
                                previousStop.location || "Previous stop"
                              })`
                            : "No previous stop — no lower bound"}
                        </strong>
                      </div>

                      <div>
                        <span>Next stop</span>

                        <strong>
                          {nextStop
                            ? `${describeSchedule(nextStop)} (${
                                nextStop.location || "Next stop"
                              })`
                            : "Last stop — no upper bound"}
                        </strong>
                      </div>
                    </div>

                    {candidateBounds?.invalid && (
                      <p className="loadevo-session-conflict-live-error">
                        The beginning of this time window cannot be later
                        than its end.
                      </p>
                    )}

                    {previewConflict && !candidateBounds?.invalid && (
                      <p className="loadevo-session-conflict-live-error">
                        The proposed schedule still has a conflict between{" "}
                        <strong>
                          {previewPoints[previewConflict.blockingIndex]?.location ||
                            "Previous stop"}
                        </strong>
                        {" and "}
                        <strong>
                          {previewPoints[previewConflict.index]?.location ||
                            "Next stop"}
                        </strong>.
                      </p>
                    )}

                    <p className="loadevo-session-conflict-hint">
                      Fixed times must be respected. Time windows allow
                      any feasible start within their limits. Driving,
                      loading, unloading and time-zone differences are
                      not included in this validation.
                    </p>
                  </div>

                  {modalData.error && (
                    <p
                      className="loadevo-session-conflict-error"
                      role="alert"
                    >
                      {modalData.error}
                    </p>
                  )}

                  <div className="loadevo-session-conflict-actions">
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={handleCancelModal}
                    >
                      Revert move
                    </button>

                    <button
                      type="button"
                      className="is-primary"
                      disabled={
                        isLoading ||
                        isScheduleLocked(selectedPoint)
                      }
                      onClick={() =>
                        handleDateModalConfirm(
                          editDate,
                          editHour,
                          editScheduleType,
                          editHourEnd
                        )
                      }
                    >
                      Save schedule
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {showModal && modalData && sessionCalendar && (
            <SessionScheduleCalendar
              key={`${sessionCalendar.pointId}-${sessionCalendar.value}`}
              value={sessionCalendar.value}
              locale={i18n.language || "en-GB"}
              position={{
                top: sessionCalendar.top,
                left: sessionCalendar.left,
              }}
              onClose={() => setSessionCalendar(null)}
              onSelect={(newDate) => {
                setModalData((previous) => {
                  if (!previous) return previous;

                  const index =
                    previous.editIndex ?? previous.newIndex;

                  const point = previous.newOrder?.[index];

                  if (
                    String(point?.id) !==
                    String(sessionCalendar.pointId)
                  ) {
                    return previous;
                  }

                  return {
                    ...previous,
                    editDate: newDate,
                    error: "",
                  };
                });

                setSessionCalendar(null);
              }}
            />
          )}

          {showModal && modalData && sessionSchedulePopup && (
            <SessionConflictTimePicker
              key={`session-schedule-${sessionSchedulePopup.pointId}`}
              kind={sessionSchedulePopup.kind}
              initialType={sessionSchedulePopup.scheduleType}
              initialStart={sessionSchedulePopup.hour}
              initialEnd={sessionSchedulePopup.hourEnd}
              position={{
                top: sessionSchedulePopup.top,
                left: sessionSchedulePopup.left,
              }}
              onClose={() => setSessionSchedulePopup(null)}
              onApply={applyConflictTimeSchedule}
            />
          )}

          {/* Free-text truck modal (external carriers) */}
          {freeTextTruckModalOpen && freeTextTruckCtx?.loadId && (
            <FreeTextTruckModal
            isOpen={freeTextTruckModalOpen}
            onClose={() => {
              setFreeTextTruckModalOpen(false);
              setFreeTextTruckCtx(null);
            }}
            title={
              freeTextTruckCtx.type === "DELIVERY"
                ? tl("modalTitles.freeTextTruckDelivery", "Set Delivery Truck — Load #{{id}}", {
                    id: freeTextTruckCtx.loadId,
                  })
                : tl("modalTitles.freeTextTruckPickup", "Set Pick-up Truck — Load #{{id}}", {
                    id: freeTextTruckCtx.loadId,
                  })
            }
            initialValue={""}
            maxLen={15}
            lettersOnly={true}
            onSubmit={async (value) => {
              const loadId = Number(freeTextTruckCtx.loadId);
              const cleaned = String(value || "").trim().slice(0, 15);

              const payload =
                freeTextTruckCtx.type === "DELIVERY"
                  ? { truckDeliveryInfo: `${cleaned} • manual` }
                  : { truckPickupInfo: `${cleaned} • manual` };

              // Optimistic: apply to ALL points of that load + type
              setLocations((prev) =>
                prev.map((loc) => {
                  if (loc.loadId !== loadId) return loc;
                  if (freeTextTruckCtx.type === "DELIVERY" && loc.type !== "DELIVERY") return loc;
                  if (freeTextTruckCtx.type === "PICKUP" && loc.type !== "PICKUP") return loc;
                  return { ...loc, truckId: null, truckPlate: cleaned };
                })
              );
              setResetKey((k) => k + 1);

              try {
                await axios.put(`/loads/${loadId}`, payload);
                setTimeout(() => softRefresh(), 120);
                if (refreshLoads) refreshLoads();
              } catch (e) {
                console.error("❌ Free-text truck save failed:", e);
                alert(tl("alerts.assignTruckFailed", "Failed to assign truck. Please try again."));
                setTimeout(() => softRefresh(), 150);
              } finally {
                setFreeTextTruckModalOpen(false);
                setFreeTextTruckCtx(null);
              }
            }}
          />
        )}

        {/* Free-text driver modal (external carriers) */}
        {freeTextDriverModalOpen && freeTextDriverCtx?.loadId && (
          <FreeTextDriverModal
            isOpen={freeTextDriverModalOpen}
            onClose={() => {
              setFreeTextDriverModalOpen(false);
              setFreeTextDriverCtx(null);
            }}
            title={
              freeTextDriverCtx.type === "DELIVERY"
                ? tl("modalTitles.freeTextDriverDelivery", "Set Delivery Driver — Load #{{id}}", {
                    id: freeTextDriverCtx.loadId,
                  })
                : tl("modalTitles.freeTextDriverPickup", "Set Pick-up Driver — Load #{{id}}", {
                    id: freeTextDriverCtx.loadId,
                  })
            }
            initialValue={""}
            maxLen={40}
            lettersOnly={true}
            placeholder={tl("placeholders.driverName", "e.g. João Silva")}
            onSubmit={async (value) => {
              const loadId = Number(freeTextDriverCtx.loadId);
              const cleaned = String(value || "").trim().slice(0, 40);
              if (!cleaned) return;

              const payload =
                freeTextDriverCtx.type === "DELIVERY"
                  ? { driverDeliveryInfo: `${cleaned} • manual` }
                  : { driverPickupInfo: `${cleaned} • manual` };

              // Optimistic: apply to ALL points of that load + type
              setLocations((prev) =>
                prev.map((loc) => {
                  if (Number(loc.loadId) !== loadId) return loc;
                  if (freeTextDriverCtx.type === "DELIVERY" && loc.type !== "DELIVERY") return loc;
                  if (freeTextDriverCtx.type === "PICKUP" && loc.type !== "PICKUP") return loc;
                  return { ...loc, driverId: null, driverName: cleaned };
                })
              );
              setResetKey((k) => k + 1);

              try {
                await axios.put(`/loads/${loadId}`, payload);
                setTimeout(() => softRefresh(), 120);
                if (refreshLoads) refreshLoads();
              } catch (e) {
                console.error("❌ Free-text driver save failed:", e);
                alert(tl("alerts.assignDriverFailed", "Failed to assign driver. Please try again."));
                setTimeout(() => softRefresh(), 150);
              } finally {
                setFreeTextDriverModalOpen(false);
                setFreeTextDriverCtx(null);
              }
            }}
          />
        )}

        {truckModalOpen && selectedPD && (
          <TruckSelectorModal
            isOpen={truckModalOpen}
            onClose={() => setTruckModalOpen(false)}
            onSelect={(truck, selectedType, alsoAssignToDeparture) => {
              const tid = truck?.id ?? null;

              // ✅ aceita manual selection (quickMode)
              const manualPlate =
                truck?.plate ??
                truck?.truckPlate ??
                truck?.manualPlate ??
                null;

              if (!tid && !manualPlate) return;

              const apply = (role) => {
                handleTruckAssign(
                  selectedPD.id,
                  tid,
                  selectedPD.type,          // mantém "STRATEGICPOINT" quando for
                  role,
                  manualPlate               // ✅ novo
                );
              };

              if (
                alsoAssignToDeparture &&
                selectedPD?.type === "STRATEGICPOINT" &&
                !isFuelStrategicPoint(selectedPD)
              ) {
                apply("arrival");
                apply("departure");
              } else {
                apply(selectedPD.truckRole || "arrival");
              }

              setTruckModalOpen(false);
              setSelectedPD(null);
            }}
            selectedType={selectedPD.type}
            selectedPickupDelivery={selectedPD}
            currentLoad={(() => {
              const l =
                selectedPD.load ||
                loads.find((x) => Number(x.id) === Number(selectedPD.loadId)) ||
                null;

              if (!l) return null;

              return { ...l, isExternalCarrier: isExternalTransportCarrier(l) };
            })()}
            title={
              selectedPD.type === "STRATEGICPOINT"
                ? tl(
                    "modalTitles.assignTruckStrategicPoint",
                    "Assign truck to strategic point"
                  )
                : tl(
                    "modalTitles.assignTruckToPoint",
                    "Assign truck to {{type}} #{{id}}",
                    { type: typeLabel(selectedPD.type), id: selectedPD.id }
                  )
            }
            allPickups={selectedPD.type === "STRATEGICPOINT" ? [] : getAllPickups(loads, selectedPD.loadId)}
            allDeliveries={selectedPD.type === "STRATEGICPOINT" ? [] : getAllDeliveries(loads, selectedPD.loadId)}
          />
        )}

        {trailerModalOpen && selectedPDForTrailer && (
          <TrailerSelectorModal
            isOpen={trailerModalOpen}
            onClose={() => {
              setTrailerModalOpen(false);
              setSelectedPDForTrailer(null);
            }}
            onSelect={(trailer) => {
              // ✅ aceita tanto trailer interno (id) como manual (plate)
              const tid = trailer?.id ?? null;
              const plate = trailer?.trailerPlate ?? trailer?.trailerNumber ?? trailer?.plate ?? trailer?.manualPlate ?? null;

              if (!tid && !plate) return;

              if (
                String(selectedPDForTrailer?.type || "").toUpperCase() === "STRATEGICPOINT" &&
                selectedPDForTrailer?.trailerRole === "both"
              ) {
                handleTrailerAssign(
                  selectedPDForTrailer.id,
                  tid,
                  selectedPDForTrailer.type,
                  "arrival",
                  plate
                );

                handleTrailerAssign(
                  selectedPDForTrailer.id,
                  tid,
                  selectedPDForTrailer.type,
                  "departure",
                  plate
                );
              } else {
                handleTrailerAssign(
                  selectedPDForTrailer.id,
                  tid,
                  selectedPDForTrailer.type,
                  selectedPDForTrailer.trailerRole,
                  plate
                );
              }

              setTrailerModalOpen(false);
              setSelectedPDForTrailer(null);
            }}
            selectedPickupDelivery={selectedPDForTrailer}
            currentLoad={(() => {
              const l =
                loads.find((x) => Number(x.id) === Number(selectedPDForTrailer.loadId)) ||
                selectedPDForTrailer.load ||
                null;

              if (!l) return null;

              // ensure quickMode works
              return { ...l, isExternalCarrier: isExternalTransportCarrier(l) };
            })()}
            title={tl("modalTitles.assignTrailerToPoint", "Assign trailer")}
            selectedType={
              String(selectedPDForTrailer?.type || "").toUpperCase() === "DELIVERY"
                ? "delivery"
                : "pickup"
            }
            mode="single"
          />
        )}

        {driverModalOpen && selectedPDForDriver && (
          <DriverSelectorModal
            isOpen={driverModalOpen}
            driverSlot={selectedDriverSlot}
            onClose={() => setDriverModalOpen(false)}
            setIsDriverUpdating={() => {}}
            onSelect={(driver, selectedType, alsoAssignToDeparture, driverSlotFromModal = selectedDriverSlot) => {
              if (!driver?.id) return;

              const name = getDriverDisplayName(driver);
              const slot = Number(driverSlotFromModal) === 2 ? 2 : 1;

              if (
                alsoAssignToDeparture &&
                selectedPDForDriver?.type === "STRATEGICPOINT" &&
                !isFuelStrategicPoint(selectedPDForDriver)
              ) {
                if (selectedPDForDriver.driverRole === "arrival") {
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "arrival", slot);
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "departure", slot);
                } else if (selectedPDForDriver.driverRole === "departure") {
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "departure", slot);
                  handleDriverAssign(selectedPDForDriver.id, driver.id, name, "STRATEGICPOINT", "arrival", slot);
                }
              } else {
                handleDriverAssign(
                  selectedPDForDriver.id,
                  driver.id,
                  name,
                  selectedPDForDriver.type,
                  selectedPDForDriver.driverRole,
                  slot
                );
              }

              setDriverModalOpen(false);
              setSelectedPDForDriver(null);
            }}
            selectedType={selectedPDForDriver.type}
            selectedPickupDelivery={selectedPDForDriver}
            currentLoad={(() => {
              const l =
                selectedPDForDriver.load ||
                loads.find((x) => Number(x.id) === Number(selectedPDForDriver.loadId)) ||
                null;

              if (!l) return null;

              return { ...l, isExternalCarrier: isExternalTransportCarrier(l) };
            })()}
            title={
              selectedPDForDriver.type === "STRATEGICPOINT"
                ? tl(
                    "modalTitles.assignDriverStrategicPoint",
                    "Assign driver to strategic point"
                  )
                : tl(
                    "modalTitles.assignDriverToPoint",
                    "Assign driver to {{type}} #{{id}}",
                    { type: typeLabel(selectedPDForDriver.type), id: selectedPDForDriver.id }
                  )
            }
            allPickups={
              selectedPDForDriver.type ===
              "STRATEGICPOINT"
                ? []
                : locations.filter(
                    (
                      point
                    ) =>
                      Number(
                        point?.loadId
                      ) ===
                        Number(
                          selectedPDForDriver
                            .loadId
                        ) &&
                      String(
                        point?.type ||
                          ""
                      ).toUpperCase() ===
                        "PICKUP"
                  )
            }
            allDeliveries={
              selectedPDForDriver.type ===
              "STRATEGICPOINT"
                ? []
                : locations.filter(
                    (
                      point
                    ) =>
                      Number(
                        point?.loadId
                      ) ===
                        Number(
                          selectedPDForDriver
                            .loadId
                        ) &&
                      String(
                        point?.type ||
                          ""
                      ).toUpperCase() ===
                        "DELIVERY"
                  )
            }
          />
        )}

        <StrategicPointModal
          isOpen={strategicModalOpen}
          onClose={() => setStrategicModalOpen(false)}
          onSubmit={async (data) => {
            try {
              await axios.post("/pickupdeliveries", {
                ...data,
                strategicPointPurpose:
                  String(data?.strategicPointPurpose || "GENERAL").toUpperCase() === "FUEL"
                    ? "FUEL"
                    : "GENERAL",
                hasTrailerSwap: !!data?.hasTrailerSwap,
                transportsessionId,
                type: "STRATEGICPOINT",
              });

              setStrategicModalOpen(false);
              fetchLocations();
              if (refreshLoads) refreshLoads();
            } catch (err) {
              alert(tl("alerts.addStrategicPointFailed", "Failed to add strategic point."));
            }
          }}
        />

        <EditStrategicPointModal
          isOpen={editStrategicModalOpen}
          onClose={() => {
            setEditStrategicModalOpen(false);
            setSelectedStrategicPointToEdit(null);
          }}
          strategicPoint={selectedStrategicPointToEdit}
          transportsessionId={transportsessionId}
          existingDateHours={existingDateHours}
          onSaved={() => {
            fetchLocations();
            if (refreshLoads) refreshLoads();
          }}
        />

        {mapModalOpen && (
          <TransportSessionMapModal
            isOpen={mapModalOpen}
            onClose={() => setMapModalOpen(false)}
            transportsessionId={transportsessionId}
            points={locationsWithLock}
            onReorderPoints={handleMapReorder}
          />
        )}
        </ModalPortal>

        <div
          className={[
            "transport-session-motion",
            sessionMotionReady && !isClosingSession ? "is-open" : "",
            isClosingSession ? "is-closing" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="transport-session-motion-inner">
            <div className="transport-session-actions-row">
              <div className="loadevo-board-actions">
                <button
                  type="button"
                  className="transport-session-map-btn"
                  disabled={isLoading}
                  onClick={() =>
                    navigate(
                      `/map?sessionId=${encodeURIComponent(transportsessionId)}`
                    )
                  }
                >
                  <FaMapMarkerAlt />
                  Ver mapa
                </button>

                <button
                  type="button"
                  className="add-strategic-point-btn"
                  disabled={isLoading || effectiveFreezeStrategicPoints}
                  onClick={() => setStrategicModalOpen(true)}
                >
                  + ADD STRATEGIC POINT
                </button>
              </div>
            </div>

            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis]}
              autoScroll={false}
              onDragStart={() => {
                isReordering.current = true;
                setDragInProgress(true);
              }}
              onDragEnd={onTimelineDragEnd}
              onDragCancel={() => {
                setDragInProgress(false);
                releaseReorder();
              }}
            >
              <SortableContext
                items={locationsWithLock.map((point) => String(point.id))}
                strategy={horizontalListSortingStrategy}
              >
            <div
              ref={listRef}
              className={`location-timeline loadevo-session-compact ${isClosingSession ? "is-closing" : ""}`}
              data-sortable-reset={resetKey}
              data-stop-count={locationsWithLock.length}
            >
              {locationsWithLock.map((location, index) => (
                <SessionSortableStop
                  key={`${location.type}-${location.id}`}
                  id={location.id}
                  dragInProgress={dragInProgress}
                  disabled={
                    isLoading ||
                    showModal ||
                    isClosingSession ||
                    isReorderLocked(location)
                  }
                >
                  {({
                    setNodeRef,
                    setActivatorNodeRef,
                    attributes,
                    listeners,
                    style,
                    isDragging,
                  }) => (
              <div
                ref={setNodeRef}
                style={style}
                className={`location-item ${location.isLocked ? "status-locked" : ""} ${
                  location.isFrozen ? "frozen" : ""
                } ${isDragging ? "loadevo-stop-dragging" : ""}`}
                data-id={location.id}
                data-type={location.type}
                data-status={String(location.status || "PENDING").toUpperCase()}
                data-location={location.location}
                data-date={location.date || ""}
                data-hour={location.hour || "00:00"}
                data-loadid={location.loadId}
              >
                <span className="loadevo-session-step-number">
                  {index + 1}
                </span>

                <div
                  className="drag-handle"
                  ref={setActivatorNodeRef}
                  {...attributes}
                  {...listeners}
                >
                  <FaGripLines
                    size={18}
                    style={{
                      cursor: (location.isFrozen || CLOSED_STATUSES.includes(String(location.status || "").toUpperCase())) ? "not-allowed" : "grab",
                      opacity: (location.isFrozen || CLOSED_STATUSES.includes(String(location.status || "").toUpperCase())) ? 0.4 : 1,
                    }}
                    title={
                      (location.isFrozen || CLOSED_STATUSES.includes(String(location.status || "").toUpperCase()))
                        ? tl("tooltips.statusLocked", "Editing is locked")
                        : tl("tooltips.dragToReorder", "Drag to reorder")
                    }
                  />
                </div>

                <div className="icon-container">
                  {location.type === "PICKUP" ? (
                    <div className="pickup-icon" />
                  ) : location.type === "DELIVERY" ? (
                    <FaMapMarkerAlt className="delivery-icon" />
                  ) : location.type === "STRATEGICPOINT" ? (
                    isFuelStrategicPoint(location) ? (
                      <FaGasPump className="strategic-point-icon fuel-strategic-point-icon" />
                    ) : (
                      <FaBullseye className="strategic-point-icon" />
                    )
                  ) : null}
                </div>

                <div className="location-info">
                  <div className="location-header">
                    <span className="location-label">
                      {location.type === "PICKUP"
                        ? tl("labels.pickupPlace", "PICK-UP PLACE")
                        : location.type === "DELIVERY"
                        ? tl("labels.deliveryPlace", "DELIVERY PLACE")
                        : location.type === "STRATEGICPOINT"
                        ? isFuelStrategicPoint(location)
                          ? tl("labels.fuelStrategicPoint", "FUEL STOP")
                          : tl("labels.strategicPoint", "STRATEGIC POINT")
                        : tl("labels.location", "LOCATION")}
                    </span>
                    <span
                      className="location-name"
                      title={location.location || "Unnamed location"}
                    >
                      {location.location || "Unnamed location"}
                    </span>

                    {(location.city || location.country || location.postalCode) && (
                      <div className="loadevo-session-stop-address">
                        <FaMapMarkerAlt aria-hidden="true" />

                        {getCountryFlag(location.country) && (
                          <img
                            className="loadevo-session-stop-flag"
                            src={getCountryFlag(location.country)}
                            alt={location.country}
                            loading="lazy"
                          />
                        )}

                        <span>
                          {[location.city, location.country]
                            .filter(Boolean)
                            .join(", ")}
                        </span>

                        {location.postalCode && (
                          <span className="loadevo-session-stop-postcode">
                            {location.postalCode}
                          </span>
                        )}
                      </div>
                    )}

                    {location.date && (
                      <div className="location-date-hour">
                        <span className="location-date">
                          {formatDateLocal(location.date)}
                        </span>
                        {location.hour && (
                          <span className="location-hour">
                            {String(location.hour).slice(0, 5)}
                            {String(location.scheduleType || "").toUpperCase() === "WINDOW" &&
                            location.hourEnd
                              ? `–${String(location.hourEnd).slice(0, 5)}`
                              : ""}
                          </span>
                        )}
                      </div>
                    )}

                    {location.type !== "STRATEGICPOINT" ? (
                      <span className="load-id">
                        {tl("labels.loadId", "LOAD ID: {{loadId}}", {
                          loadId: getCompanyLoadNumber(location),
                        })}
                      </span>
                    ) : (
                      <span className="load-id load-id-placeholder">&nbsp;</span>
                    )}
                  </div>

                  {location.type === "STRATEGICPOINT" ? (
                    !hasTrailerSwapEnabled(location) ? (
                      <>
                        {/* Strategic point without trailer swap: single TRUCK / DRIVER / TRAILER */}
                        <div className="strategic-inline-assignments">
                          <div className="strategic-row">
                          <div className="assign-truck-btn-wrapper strategic-arrival strategic-inline-truck">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.fuelTruck", "Truck:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTruck(location, "arrival") ? "assigned-truck-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTruckAssignUI(location, "arrival");
                                }}
                                title={
                                  isAssignmentBlockedNoAlert(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignTruck", "Assign Truck")
                                }
                              >
                                <TruckGlyph className="assign-truck-icon" />
                                <span className="assign-truck-label">
                                  {location.truckArrivalPlate
                                    ? location.truckArrivalPlate
                                    : location.truckIdArrival
                                    ? `#${location.truckIdArrival}`
                                    : tl("buttons.assignFuelTruck", "Assign Truck")}
                                </span>
                              </button>

                              {(location.truckIdArrival || location.truckArrivalPlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentBlockedNoAlert(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTruck", "Remove truck")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTruck(location.id, "arrival");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="assign-truck-btn-wrapper strategic-arrival strategic-inline-drivers">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.fuelDriver", "Driver:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 1) ? "assigned-driver-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openDriverAssignUI(location, "arrival", 1);
                                }}
                                title={
                                  isAssignmentBlockedNoAlert(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignFuelDriver1", "Assign Driver 1")
                                }
                              >
                                <User className="assign-driver-icon" size={16} />
                                <span className="assign-driver-label">
                                  {location.driverNameArrival
                                    ? location.driverNameArrival
                                    : location.driverIdArrival
                                    ? tl("labels.driverAssigned", "Driver assigned")
                                    : tl("buttons.assignFuelDriver1", "Assign Driver 1")}
                                </span>
                              </button>

                              {(location.driverIdArrival || location.driverNameArrival) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeDriver1", "Remove driver 1")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveDriver(location.id, "arrival", 1);
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 2) ? "assigned-driver-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openDriverAssignUI(location, "arrival", 2);
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignFuelDriver2", "Assign Driver 2")
                                }
                              >
                                <User className="assign-driver-icon" size={16} />
                                <span className="assign-driver-label">
                                  {location.driver2NameArrival
                                    ? location.driver2NameArrival
                                    : location.driver2IdArrival
                                    ? tl("labels.driverAssigned", "Driver assigned")
                                    : tl("buttons.assignFuelDriver2", "Assign Driver 2")}
                                </span>
                              </button>

                              {(location.driver2IdArrival || location.driver2NameArrival) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeDriver2", "Remove driver 2")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveDriver(location.id, "arrival", 2);
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="strategic-row">
                          <div className="assign-truck-btn-wrapper strategic-arrival strategic-inline-trailer">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.fuelTrailerOptional", "Trailer (optional):")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTrailer(location, "arrival") ? "assigned-trailer-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "arrival");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignFuelTrailerOptional", "Assign Trailer (optional)")
                                }
                              >
                                <TrailerGlyph className="assign-truck-icon trailer-icon" />
                                <span className="assign-truck-label">
                                  {location.trailerArrivalPlate
                                    ? location.trailerArrivalPlate
                                    : location.trailerIdArrival
                                    ? (trailersById[String(location.trailerIdArrival)] || `#${location.trailerIdArrival}`)
                                    : tl("buttons.assignFuelTrailerOptional", "Assign Trailer (optional)")}
                                </span>
                              </button>

                              {(location.trailerIdArrival || location.trailerArrivalPlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "arrival");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        </div>
                      </>
                    ) : (
                      <>
                      {/* Arrival / Departure TRUCK */}
                      <div className="strategic-row">
                        <div className="assign-truck-btn-wrapper strategic-arrival">
                          <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.arrivalTruck", "Arrival Truck:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedTruck(location, "arrival") ? "assigned-truck-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openTruckAssignUI(location, "arrival");
                              }}
                              title={
                                isAssignmentBlockedNoAlert(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignTruck", "Assign Truck")
                              }
                            >
                              <TruckGlyph className="assign-truck-icon" />
                              <span className="assign-truck-label">
                                {location.truckArrivalPlate
                                  ? location.truckArrivalPlate
                                  : location.truckIdArrival
                                  ? `#${location.truckIdArrival}`
                                  : tl("buttons.assignTruck", "Assign Truck")}
                              </span>
                            </button>

                            {(location.truckIdArrival || location.truckArrivalPlate) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeTruck", "Remove truck")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveTruck(location.id, "arrival");
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="assign-truck-btn-wrapper strategic-departure">
                          <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.departureTruck", "Departure Truck:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedTruck(location, "departure") ? "assigned-truck-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openTruckAssignUI(location, "departure");
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignDepartureTruck", "Assign Departure Truck")
                              }
                            >
                              <TruckGlyph className="assign-truck-icon" />
                              <span className="assign-truck-label">
                                {location.truckDeparturePlate
                                  ? location.truckDeparturePlate
                                  : location.truckIdDeparture
                                  ? `#${location.truckIdDeparture}`
                                  : tl("buttons.assignDepartureTruck", "Assign Departure Truck")}
                              </span>
                            </button>

                            {(location.truckIdDeparture || location.truckDeparturePlate) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeTruck", "Remove truck")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveTruck(location.id, "departure");
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* TRAILER — 1 field by default, 2 fields when trailer swap is enabled */}
                      {hasTrailerSwapEnabled(location) ? (
                        <div className="strategic-row">
                          <div className="assign-truck-btn-wrapper strategic-arrival">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.arrivalTrailer", "Arrival Trailer:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTrailer(location, "arrival") ? "assigned-trailer-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "arrival");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignTrailer", "Assign Trailer")
                                }
                              >
                                <TrailerGlyph className="assign-truck-icon trailer-icon" />
                                <span className="assign-truck-label">
                                  {location.trailerArrivalPlate
                                    ? location.trailerArrivalPlate
                                    : location.trailerIdArrival
                                    ? (trailersById[String(location.trailerIdArrival)] || `#${location.trailerIdArrival}`)
                                    : tl("buttons.assignTrailer", "Assign Trailer")}
                                </span>
                              </button>

                              {(location.trailerIdArrival || location.trailerArrivalPlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "arrival");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="assign-truck-btn-wrapper strategic-departure">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.departureTrailer", "Departure Trailer:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${hasAssignedTrailer(location, "departure") ? "assigned-trailer-btn" : ""}`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "departure");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignDepartureTrailer", "Assign Departure Trailer")
                                }
                              >
                                <TrailerGlyph className="assign-truck-icon trailer-icon" />
                                <span className="assign-truck-label">
                                  {location.trailerDeparturePlate
                                    ? location.trailerDeparturePlate
                                    : location.trailerIdDeparture
                                    ? (trailersById[String(location.trailerIdDeparture)] || `#${location.trailerIdDeparture}`)
                                    : tl("buttons.assignDepartureTrailer", "Assign Departure Trailer")}
                                </span>
                              </button>

                              {(location.trailerIdDeparture || location.trailerDeparturePlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "departure");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="strategic-row strategic-row-single">
                          <div className="assign-truck-btn-wrapper strategic-single-trailer">
                            <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                              {tl("labels.trailer", "Trailer:")}
                            </span>

                            <div className="strategic-truck-group">
                              <button
                                className={`assign-truck-btn ${
                                  hasAssignedTrailer(location, "arrival") || hasAssignedTrailer(location, "departure")
                                    ? "assigned-trailer-btn"
                                    : ""
                                }`}
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  openTrailerAssignUI(location, "both");
                                }}
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("buttons.assignTrailer", "Assign Trailer")
                                }
                              >
                                <TrailerGlyph className="assign-truck-icon trailer-icon" />
                                <span className="assign-truck-label">
                                  {location.trailerArrivalPlate
                                    ? location.trailerArrivalPlate
                                    : location.trailerDeparturePlate
                                    ? location.trailerDeparturePlate
                                    : location.trailerIdArrival
                                    ? (trailersById[String(location.trailerIdArrival)] || `#${location.trailerIdArrival}`)
                                    : location.trailerIdDeparture
                                    ? (trailersById[String(location.trailerIdDeparture)] || `#${location.trailerIdDeparture}`)
                                    : tl("buttons.assignTrailer", "Assign Trailer")}
                                </span>
                              </button>

                              {(location.trailerIdArrival ||
                                location.trailerArrivalPlate ||
                                location.trailerIdDeparture ||
                                location.trailerDeparturePlate) && (
                                <button
                                  className="remove-truck-btn strategic-remove-btn"
                                  title={
                                    isAssignmentLocked(location)
                                      ? getBlockedAssignmentHoverMessage(location)
                                      : tl("tooltips.removeTrailer", "Remove trailer")
                                  }
                                  disabled={getAssignmentButtonDisabled(location)}
                                  onClick={() => {
                                    if (guardAssignmentAction(location)) return;

                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "arrival");
                                    handleRemoveTrailer(location.id, "STRATEGICPOINT", "departure");
                                  }}
                                  tabIndex={-1}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Arrival / Departure DRIVER */}
                      <div className="strategic-row">
                        <div className="assign-truck-btn-wrapper strategic-arrival">
                          <span className="truck-role-label strategic-arrival-driver-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.arrivalDriver", "Arrival Driver:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 1) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "arrival", 1);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignArrivalDriver1", "Assign Arrival Driver 1")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driverNameArrival
                                  ? location.driverNameArrival
                                  : location.driverIdArrival
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignArrivalDriver1", "Assign Arrival Driver 1")}
                              </span>
                            </button>

                            {(location.driverIdArrival || location.driverNameArrival) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentBlockedNoAlert(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeArrivalDriver1", "Remove arrival driver 1")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "arrival", 1);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "arrival", 2) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "arrival", 2);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignArrivalDriver2", "Assign Arrival Driver 2")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driver2NameArrival
                                  ? location.driver2NameArrival
                                  : location.driver2IdArrival
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignArrivalDriver2", "Assign Arrival Driver 2")}
                              </span>
                            </button>

                            {(location.driver2IdArrival || location.driver2NameArrival) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeArrivalDriver2", "Remove arrival driver 2")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "arrival", 2);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="assign-truck-btn-wrapper strategic-departure">
                          <span className="truck-role-label" style={{ display: "block", marginBottom: 2 }}>
                            {tl("labels.departureDriver", "Departure Driver:")}
                          </span>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "departure", 1) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "departure", 1);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignDepartureDriver1", "Assign Departure Driver 1")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driverNameDeparture
                                  ? location.driverNameDeparture
                                  : location.driverIdDeparture
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignDepartureDriver1", "Assign Departure Driver 1")}
                              </span>
                            </button>

                            {(location.driverIdDeparture || location.driverNameDeparture) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeDepartureDriver1", "Remove departure driver 1")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "departure", 1);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>

                          <div className="strategic-truck-group">
                            <button
                              className={`assign-truck-btn ${hasAssignedDriver(location, "departure", 2) ? "assigned-driver-btn" : ""}`}
                              disabled={getAssignmentButtonDisabled(location)}
                              onClick={() => {
                                if (guardAssignmentAction(location)) return;
                                openDriverAssignUI(location, "departure", 2);
                              }}
                              title={
                                isAssignmentLocked(location)
                                  ? getBlockedAssignmentHoverMessage(location)
                                  : tl("buttons.assignDepartureDriver2", "Assign Departure Driver 2")
                              }
                            >
                              <User className="assign-driver-icon" size={16} />
                              <span className="assign-driver-label">
                                {location.driver2NameDeparture
                                  ? location.driver2NameDeparture
                                  : location.driver2IdDeparture
                                  ? tl("labels.driverAssigned", "Driver assigned")
                                  : tl("buttons.assignDepartureDriver2", "Assign Departure Driver 2")}
                              </span>
                            </button>

                            {(location.driver2IdDeparture || location.driver2NameDeparture) && (
                              <button
                                className="remove-truck-btn strategic-remove-btn"
                                title={
                                  isAssignmentLocked(location)
                                    ? getBlockedAssignmentHoverMessage(location)
                                    : tl("tooltips.removeDepartureDriver2", "Remove departure driver 2")
                                }
                                disabled={getAssignmentButtonDisabled(location)}
                                onClick={() => {
                                  if (guardAssignmentAction(location)) return;
                                  handleRemoveDriver(location.id, "departure", 2);
                                }}
                                tabIndex={-1}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )
                ) : (
                    <>
                      {/* Truck for PICKUP/DELIVERY */}
                      <div className="assign-truck-btn-wrapper loadevo-resource-truck">
                        <button
                          className={`assign-truck-btn ${hasAssignedTruck(location) ? "assigned-truck-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openTruckAssignUI(location, location.type.toLowerCase()); // pickup | delivery
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignTruck", "Assign Truck")
                          }
                        >
                          <TruckGlyph className="assign-truck-icon" />
                          <span className="assign-truck-label">
                            {location.truckPlate ||
                              trucksById[String(location.truckId)] ||
                              (location.truckId
                                ? tl("labels.truckAssigned", "Truck assigned")
                                : tl("buttons.assignTruck", "Assign Truck"))}
                          </span>
                        </button>

                        {(location.truckPlate || location.truckId) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeTruck", "Remove truck")
                            }
                            disabled={getAssignmentButtonDisabled(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveTruck(location.id, location.type.toLowerCase());
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      {/* Trailer for PICKUP/DELIVERY */}
                      <div className="assign-truck-btn-wrapper loadevo-resource-trailer">
                        <button
                          className={`assign-truck-btn ${hasAssignedTrailer(location) ? "assigned-trailer-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openTrailerAssignUI(location);
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignTrailer", "Assign Trailer")
                          }
                        >
                          <TrailerGlyph className="assign-truck-icon trailer-icon" />
                          <span className="assign-truck-label">
                            {location.trailerPlate ||
                              trailersById[String(location.trailerId)] ||
                              (location.trailerId
                                ? tl("labels.trailerAssigned", "Trailer assigned")
                                : tl("buttons.assignTrailer", "Assign Trailer"))}
                          </span>
                        </button>

                        {(location.trailerId || location.trailerPlate) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeTrailer", "Remove trailer")
                            }
                            disabled={isAssignmentBlockedNoAlert(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveTrailer(location.id, location.type); // 👈 passa type p/ sabermos PICKUP/DELIVERY
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                       {/* Drivers for PICKUP/DELIVERY */}
                      <div className="assign-truck-btn-wrapper">
                        <button
                          className={`assign-truck-btn ${hasAssignedDriver(location) ? "assigned-driver-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openDriverAssignUI(location, location.type.toLowerCase(), 1); // pickup | delivery
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignDriver1", "Assign Driver 1")
                          }
                        >
                          <User className="assign-driver-icon" size={16} />
                          <span className="assign-driver-label">
                            {location.driverName
                              ? location.driverName
                              : location.driverId
                              ? tl("labels.driverAssigned", "Driver assigned")
                              : tl("buttons.assignDriver1", "Driver 1")}
                          </span>
                        </button>

                        {(location.driverId || location.driverName) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeDriver1", "Remove driver 1")
                            }
                            disabled={isAssignmentBlockedNoAlert(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveDriver(location.id, location.type.toLowerCase(), 1);
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      <div className="assign-truck-btn-wrapper">
                        <button
                          className={`assign-truck-btn ${location.driver2Id || location.driver2Name ? "assigned-driver-btn" : ""}`}
                          disabled={getAssignmentButtonDisabled(location)}
                          onClick={() => {
                            if (guardAssignmentAction(location)) return;
                            openDriverAssignUI(location, location.type.toLowerCase(), 2); // pickup | delivery
                          }}
                          style={{
                            cursor: isAssignmentBlockedNoAlert(location) ? "not-allowed" : "pointer",
                          }}
                          title={
                            isAssignmentBlockedNoAlert(location)
                              ? getBlockedAssignmentHoverMessage(location)
                              : tl("buttons.assignDriver2", "Assign Driver 2")
                          }
                        >
                          <User className="assign-driver-icon" size={16} />
                          <span className="assign-driver-label">
                            {location.driver2Name
                              ? location.driver2Name
                              : location.driver2Id
                              ? tl("labels.driverAssigned", "Driver assigned")
                              : tl("buttons.assignDriver2", "Driver 2")}
                          </span>
                        </button>

                        {(location.driver2Id || location.driver2Name) && (
                          <button
                            className="remove-truck-btn"
                            title={
                              isAssignmentBlockedNoAlert(location)
                                ? getBlockedAssignmentHoverMessage(location)
                                : tl("tooltips.removeDriver2", "Remove driver 2")
                            }
                            disabled={isAssignmentBlockedNoAlert(location)}
                            onClick={() => {
                              if (guardAssignmentAction(location)) return;
                              handleRemoveDriver(location.id, location.type.toLowerCase(), 2);
                            }}
                            tabIndex={-1}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* STATUS + PHOTOS */}
                <div className="assign-truck-btn-wrapper status-actions-wrapper">
                  {(() => {
                    const statusLocked =
                      (location.isStatusLocked ?? location.isLocked) || location.isFrozen;

                    const blockedStatusHoverMessage = getBlockedStatusHoverMessage();

                    const hasRealPhotos =
                      photoPresenceById[String(location.id)] === true;

                    return (
                      <>
                        {(location.type === "PICKUP" || location.type === "DELIVERY") && (
                          <div className="point-media-actions-row">
                            <button
                              className={`assign-truck-btn cmr-photo-btn ${
                                hasRealPhotos ? "photo-btn assigned-photo-btn" : ""
                              }`}
                              style={
                                hasRealPhotos
                                  ? undefined
                                  : {
                                      background: "rgba(133, 148, 171, 0.14)",
                                      border: "1px solid rgba(133, 148, 171, 0.34)",
                                      boxShadow: "none",
                                      color: "var(--text, #eaf2ff)",
                                    }
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPdForPhotos(location.id);
                                setCmrLoadModalOpen(true);
                              }}
                              title={tl("tooltips.photos", "Upload / View photos")}
                            >
                              <Camera
                                className="assign-photo-icon"
                                size={20}
                                strokeWidth={2}
                                aria-hidden="true"
                                style={
                                  hasRealPhotos
                                    ? undefined
                                    : { color: "var(--text, #eaf2ff)" }
                                }
                              />
                              <span
                                className="assign-photo-label"
                                style={
                                  hasRealPhotos
                                    ? undefined
                                    : { color: "var(--text, #eaf2ff)" }
                                }
                              >
                                {tl("buttons.photos", "Photos")}
                              </span>
                            </button>

                            {location.driverCompletionHasIssue === true &&
                              String(location.driverCompletionNote || "").trim() && (
                                <div
                                  className="driver-note-indicator-wrapper"
                                  tabIndex={0}
                                >
                                  <span
                                    className="driver-note-indicator"
                                    aria-label={tl(
                                      "driverOccurrence.noteTitle",
                                      "Nota do motorista"
                                    )}
                                    title={tl(
                                      "driverOccurrence.noteTitle",
                                      "Nota do motorista"
                                    )}
                                  >
                                    <FaCommentDots aria-hidden="true" />
                                  </span>

                                  <div className="driver-note-popover">
                                    <div className="driver-note-popover-header">
                                      <FaCommentDots
                                        className="driver-note-popover-header-icon"
                                        aria-hidden="true"
                                      />

                                      <span className="driver-note-popover-title">
                                        {tl(
                                          "driverOccurrence.noteTitle",
                                          "Nota do motorista"
                                        )}
                                      </span>
                                    </div>

                                    <p className="driver-note-popover-text">
                                      {String(
                                        location.driverCompletionNote || ""
                                      ).trim()}
                                    </p>

                                    {(location.driverCompletionNoteDriverName ||
                                      location.driverCompletionNoteAt) && (
                                      <div className="driver-note-popover-meta">
                                        {location.driverCompletionNoteDriverName && (
                                          <span>
                                            {location.driverCompletionNoteDriverName}
                                          </span>
                                        )}

                                        {location.driverCompletionNoteDriverName &&
                                          location.driverCompletionNoteAt && (
                                            <span
                                              className="driver-note-popover-meta-separator"
                                              aria-hidden="true"
                                            >
                                              ·
                                            </span>
                                          )}

                                        {location.driverCompletionNoteAt && (
                                          <span>
                                            {String(
                                              formatStatusEventDateTime({
                                                eventTimestamp:
                                                  location.driverCompletionNoteAt,
                                              }) || ""
                                            ).trim()}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                        <div
                          className={`status-tooltip-wrapper ${
                            statusLocked ? "show-status-tooltip" : ""
                          }`}
                          data-tooltip={blockedStatusHoverMessage}
                        >
                          <button
                            className={`assign-truck-btn
                              ${String(location.status || "").toUpperCase() === "COMPLETED" ? "status-completed" : ""}
                              ${String(location.status || "").toUpperCase() === "CANCELLED" ? "status-cancelled" : ""}
                              ${statusLocked ? "status-disabled" : ""}
                            `}
                            onClick={() => {
                              if (statusLocked) return;

                              const tUpper = String(location?.type || "").toUpperCase();
                              const currentUpper = String(location?.status || "").toUpperCase();

                              if (tUpper !== "STRATEGICPOINT" && currentUpper === "PENDING") {
                                const ok = canChangeStatusOffPending(location);

                                if (!ok) {
                                  alert(
                                    tl(
                                      "alerts.statusRequiresTruckDriver",
                                      "❌ You must assign BOTH a truck and a driver before changing the status."
                                    )
                                  );
                                  return;
                                }
                              }

                              setSelectedPDForStatus(location);
                              setStatusModalOpen(true);
                            }}
                            title={
                              statusLocked
                                ? blockedStatusHoverMessage
                                : tl("tooltips.statusClickToUpdate", "Click to update status")
                            }
                          >
                            {renderStatusButtonContent(location)}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Strategic actions */}
                {location.type === "STRATEGICPOINT" && (
                  <div className="assign-truck-btn-wrapper">
                    <div className="strategic-actions">
                      <button
                        className="edit-strategic-btn"
                        title={
                          isClosedStrategicPoint(location)
                            ? getClosedStrategicPointEditMessage(location)
                            : tl("tooltips.editStrategicPoint", "Edit strategic point")
                        }
                        disabled={location.isFrozen}
                        onClick={async () => {
                          if (guardStrategicPointEditAction(location)) return;

                          try {
                            setIsLoading(true);

                            const { data } = await axios.get(`/pickupdeliveries/by-id/${location.id}`);

                            const raw = data?.pickupDelivery || data?.pd || data?.row || data;

                            const normDate = (d) => {
                              if (!d) return "";
                              const s = String(d);
                              return s.includes("T") ? s.split("T")[0] : s; // YYYY-MM-DD
                            };
                            const normHour = (h) => {
                              if (!h) return "";
                              const s = String(h);
                              return s.length >= 5 ? s.slice(0, 5) : s; // HH:MM
                            };

                            const normalized = {
                              ...raw,
                              location:
                                raw?.location ??
                                raw?.place?.placeName ??
                                raw?.place?.location ??
                                raw?.placeName ??
                                location?.location ??
                                "",

                              date: normDate(raw?.date ?? location?.date),
                              hour: normHour(raw?.hour ?? location?.hour),
                              
                              strategicPointPurpose:
                                String(
                                  raw?.strategicPointPurpose ??
                                  location?.strategicPointPurpose ??
                                  "GENERAL"
                                ).toUpperCase() === "FUEL"
                                  ? "FUEL"
                                  : "GENERAL",

                              hasTrailerSwap:
                                raw?.hasTrailerSwap === true ||
                                String(raw?.hasTrailerSwap || "").toLowerCase() === "true" ||
                                location?.hasTrailerSwap === true ||
                                String(location?.hasTrailerSwap || "").toLowerCase() === "true",

                              address: raw?.address ?? raw?.place?.address ?? "",
                              postalCode: raw?.postalCode ?? raw?.place?.postalCode ?? "",
                              city: raw?.city ?? raw?.place?.city ?? "",
                              country: raw?.country ?? raw?.place?.country ?? "",
                              gMapsLink: raw?.gMapsLink ?? raw?.place?.gMapsLink ?? "",
                              coordinates: raw?.coordinates ?? raw?.place?.coordinates ?? "",
                              contactPhone: raw?.contactPhone ?? raw?.place?.contactPhone ?? "",

                              placeId:
                                raw?.placeId != null ? Number(raw.placeId) :
                                raw?.place?.id != null ? Number(raw.place.id) :
                                null,
                            };

                            setSelectedStrategicPointToEdit(normalized);
                            setEditStrategicModalOpen(true);
                          } catch (e) {
                            console.error("Failed to fetch strategic point full data:", e);
                            alert(tl("alerts.failedFetchStrategicPoint", "Failed to load strategic point details."));
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                      >
                        <FaEdit /> {tl("buttons.edit", "Edit")}
                      </button>

                      <button
                        className="remove-strategic-btn"
                        title={
                          isClosedStrategicPoint(location)
                            ? getClosedStrategicPointEditMessage(location)
                            : tl("tooltips.deleteStrategicPoint", "Delete strategic point")
                        }
                        disabled={location.isFrozen}
                        onClick={() => {
                          if (guardStrategicPointEditAction(location)) return;
                          handleDeleteStrategicPoint(location.id);
                        }}
                      >
                        <FaTrash /> {tl("buttons.delete", "Delete")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
                  )}
                </SessionSortableStop>
              ))}

              {locationsWithLock.length === 2 && (
                <button
                  type="button"
                  className="loadevo-session-connector-action"
                  disabled={isLoading || effectiveFreezeStrategicPoints}
                  onClick={() => setStrategicModalOpen(true)}
                  title={tl("tooltips.addStrategicPoint", "Add strategic point")}
                  aria-label={tl("tooltips.addStrategicPoint", "Add strategic point")}
                >
                  +
                </button>
              )}
            </div>
              </SortableContext>

            </DndContext>
          </div>
        </div>

        <ModalPortal>
          {/* Status modal */}
          <StatusSelectorModal
            isOpen={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
            currentStatus={selectedPDForStatus?.status}
          type={selectedPDForStatus?.type}
          locationName={selectedPDForStatus?.place?.location || selectedPDForStatus?.location}
          onSelect={async (newStatus) => {
            try {
              const upperNew = String(newStatus || "").toUpperCase();
              const tUpper = String(selectedPDForStatus?.type || "").toUpperCase();

              // ✅ Block: PICKUP/DELIVERY cannot leave PENDING without truck+driver
              if (tUpper !== "STRATEGICPOINT" && !isPendingStatus(upperNew)) {
                const ok = canChangeStatusOffPending(selectedPDForStatus);

                if (!ok) {
                  alert(
                    tl(
                      "alerts.statusRequiresTruckDriver",
                      "❌ You must assign BOTH a truck and a driver before changing the status."
                    )
                  );
                  return;
                }
              }

              const { data: updatedStatusPoint } = await axios.put(
                `/pickupdeliveries/${selectedPDForStatus.id}/status`,
                {
                  status: newStatus,
                }
              );

              const nextPoints = (locationsRef.current || []).map((loc) =>
                Number(loc.id) === Number(selectedPDForStatus.id)
                  ? {
                      ...loc,
                      status: newStatus,
                      latestStatusEvent:
                        updatedStatusPoint?.latestStatusEvent ??
                        loc.latestStatusEvent ??
                        null,
                    }
                  : loc
              );

              setLocations(nextPoints);
              setResetKey((prev) => prev + 1);
              onPointsChanged?.(nextPoints);

              // ✅ detect if this is the LAST DELIVERY of that load in the session timeline
              const upper = String(newStatus || "").toUpperCase();
              const isClosing = upper === "COMPLETED" || upper === "CANCELLED";

              const isDelivery = String(selectedPDForStatus?.type || "").toUpperCase() === "DELIVERY";
              const loadId = Number(selectedPDForStatus?.loadId);

              const sorted = Array.isArray(nextPoints) ? [...nextPoints] : [];
              sorted.sort(cmpLocations);

              const deliveriesSameLoad = sorted.filter(
                (p) => String(p?.type || "").toUpperCase() === "DELIVERY" && Number(p?.loadId) === loadId
              );

              const lastDelivery = deliveriesSameLoad.length
                ? deliveriesSameLoad[deliveriesSameLoad.length - 1]
                : null;

              const isLastDelivery =
                !!lastDelivery && Number(lastDelivery.id) === Number(selectedPDForStatus.id);

              setStatusModalOpen(false);

              // ✅ Last delivery closed -> CMR first, inventory only after CMR modal closes
              if (isClosing && isDelivery && isLastDelivery) {
                const updatedSelected =
                  deliveriesSameLoad.find((d) => Number(d.id) === Number(selectedPDForStatus.id)) ||
                  { ...selectedPDForStatus, status: newStatus };

                await startLastDeliveryCmrFlow(updatedSelected, loadId);
              }

              setSelectedPDForStatus(null);
              fetchLocations();
              if (refreshLoads) refreshLoads();
            } catch (err) {
              alert(tl("alerts.updateStatusFailed", "Failed to update status."));
            }
          }}
        />

        <TruckInventoryModal
          isOpen={inventoryModalOpen}
          truck={inventoryTruck}
          initialInventory={inventoryData}
          isSaving={inventorySaving}
          onClose={() => {
            setInventoryModalOpen(false);
          }}
          onContinue={async () => {
            setInventoryModalOpen(false);

            // ✅ se também existe trailer inventory preparado, mostra-o a seguir
            if (trailerMeta?.id && trailerInvData) {
              setTrailerInventoryModalOpen(true);
              return;
            }

            await finalizeAfterInventory(selectedLoadForCmr);
          }}
          onSaveAndContinue={async (vals) => {
            if (!inventoryTruck?.id) {
              setInventoryModalOpen(false);

              // ✅ chain: truck não existe -> tenta trailer -> senão finaliza
              if (trailerMeta?.id && trailerInvData) {
                setTrailerInventoryModalOpen(true);
                return;
              }

              await finalizeAfterInventory(selectedLoadForCmr);
              return;
            }

            try {
              setInventorySaving(true);
              await axios.put(`/trucks/${inventoryTruck.id}`, {
                number_of_straps: vals.number_of_straps ?? 0,
                number_of_ratchets: vals.number_of_ratchets ?? 0,
                number_of_edge_protectors: vals.number_of_edge_protectors ?? 0,
                number_of_anti_slip_mats: vals.number_of_anti_slip_mats ?? 0,
              });
            } catch (e) {
              console.error("Failed to save truck inventory:", e);
              alert(tl("alerts.saveInventoryFailedContinue", "Failed to save inventory. Continuing."));
            } finally {
              setInventorySaving(false);
              setInventoryModalOpen(false);

              // ✅ se também existe trailer inventory preparado, mostra-o a seguir
              if (trailerMeta?.id && trailerInvData) {
                setTrailerInventoryModalOpen(true);
                return;
              }

              await finalizeAfterInventory(selectedLoadForCmr);
            }
          }}
        />

        <TrailerInventoryModal
          isOpen={trailerInventoryModalOpen}
          trailer={trailerMeta}
          initialInventory={trailerInvData}
          isSaving={trailerInvSaving}
          onClose={() => {
            setTrailerInventoryModalOpen(false);
          }}
          onContinue={async () => {
            setTrailerInventoryModalOpen(false);
            await finalizeAfterInventory(selectedLoadForCmr);
          }}
          onSaveAndContinue={async (vals) => {
            if (!trailerMeta?.id) {
              setTrailerInventoryModalOpen(false);
              await finalizeAfterInventory(selectedLoadForCmr);
              return;
            }

            try {
              await axios.put(`/trailers/${trailerMeta.id}/inventory`, {
                numberOfStraps: vals.numberOfStraps ?? 0,
                numberOfRatchets: vals.numberOfRatchets ?? 0,
                numberOfEdgeProtectors: vals.numberOfEdgeProtectors ?? 0,
                numberOfAntiSlipMats: vals.numberOfAntiSlipMats ?? 0,
                numberOfWoodenBeams: vals.numberOfWoodenBeams ?? 0,
                numberOfSidebars: vals.numberOfSidebars ?? 0,
                numberOfFireExtinguishers: vals.numberOfFireExtinguishers ?? 0,
              });
            } catch (e) {
              console.error("Failed to save trailer inventory:", e);
              alert(
                tl(
                  "alerts.saveTrailerInventoryFailedContinue",
                  "Failed to save trailer inventory. Continuing."
                )
              );
            } finally {
              setTrailerInvSaving(false);
              setTrailerInventoryModalOpen(false);
              await finalizeAfterInventory(selectedLoadForCmr);
            }
          }}
        />

          <CMRLoadModal
            isOpen={cmrLoadModalOpen}
            pickupDeliveryId={selectedPdForPhotos}
            onClose={async () => {
              if (pendingInventoryAfterCmr) {
                await continuePendingInventoryAfterCmr();
                return;
              }

              const pointIdToRefresh = selectedPdForPhotos;

              setCmrLoadModalOpen(false);
              setSelectedPdForPhotos(null);

              // Verificar as fotografias realmente guardadas neste ponto
              if (pointIdToRefresh != null) {
                try {
                  const hasPhotos = await hasStoredDocPhotos(pointIdToRefresh);

                  setPhotoPresenceById((previous) => ({
                    ...previous,
                    [String(pointIdToRefresh)]: hasPhotos,
                  }));
                } catch (e) {
                  console.error(
                    "❌ Failed to refresh real CMR photos state:",
                    e
                  );
                }
              }

              try {
                await fetchLocations();
                if (refreshLoads) refreshLoads();
              } catch (e) {
                console.error("❌ Failed to refresh locations after closing CMR modal:", e);
              }
            }}
          />
        </ModalPortal>
      </>
    );
  }
);

export default TransportSessionLocations;

